// Custom, transparent browser fingerprint. Collects device/browser signals client-side and
// derives a versioned SHA-256 snapshot. Browser-only — call from a client component's effect.
//
// Signals marked `hashed: false` are shown but deliberately kept OUT of the hash: they change
// with user actions or environment (permissions, battery, plugged-in devices, free disk space)
// rather than identifying the browser, and would make a comparison misleading.

/** Why a read produced the value it did. Kept separate from the displayed text so counters
 * and verdicts never have to pattern-match prose (which silently miscounted `—`, counted one
 * WebGL failure as eight, and let a transient timeout change the hash). */
export type SignalStatus =
  /** The API answered with real data. */
  | "readable"
  /** The API doesn't exist in this browser build — absent, but deterministically absent. */
  | "unsupported"
  /** The API positively refused the read (for example, SecurityError or NotAllowedError). */
  | "blocked"
  /** The read failed for an unknown implementation, resource, driver, or extension reason. */
  | "error"
  /** The read didn't settle in time. Transient, so never hashed. */
  | "timeout";

/** Only a successful value is a hash input. Unsupported APIs and every failure state are
 * represented in the report, but never as magic placeholder strings in the hash. */
export function isHashableSignalStatus(status: SignalStatus): boolean {
  return status === "readable";
}

/** A thrown exception is neutral unless it positively identifies an intentional browser
 * refusal. This prevents OOMs, context loss, driver faults, and implementation bugs from being
 * sold to the user as privacy protection. */
function statusFromException(error: unknown): Extract<SignalStatus, "blocked" | "error"> {
  if (
    error instanceof DOMException &&
    (error.name === "SecurityError" || error.name === "NotAllowedError")
  ) {
    return "blocked";
  }
  return "error";
}

export type VectorOutcome = "read" | "scrambled" | "unstable" | "blocked" | "unavailable";

export interface RepeatedProbeAssessment {
  outcome: VectorOutcome;
  /** Both independent reads succeeded and returned the same value. */
  repeatableInScan: boolean;
  /** Both independent reads succeeded but returned different values. */
  randomizedInScan: boolean;
  /** The result cannot support either a stable-value or a defence claim. */
  inconclusive: boolean;
  /** The repeated value is eligible for the canonical snapshot. */
  hashable: boolean;
  /** Status used by the display row; never disguises a mixed result as a successful read. */
  signalStatus: SignalStatus;
}

/** Classify two independent reads without inferring intent from an exception. A changed
 * known-value check is strong evidence that the returned test data was modified — the only
 * observation that earns the "scrambled" verdict. Two successful reads that merely disagree
 * prove the observable value changed, not why: resource pressure, a driver fault, settling
 * font availability or a non-privacy extension all produce the same symptom, so that case is
 * reported as neutral instability ("varied"), never as a privacy defence. Matching reads still
 * only establish repeatability—not originality. */
export function assessRepeatedProbes(
  a: Probe,
  b: Probe,
  knownValueAltered = false
): RepeatedProbeAssessment {
  if (a.status === "readable" && b.status === "readable") {
    const repeatableInScan = a.value === b.value;
    if (knownValueAltered) {
      return {
        outcome: "scrambled",
        repeatableInScan,
        randomizedInScan: !repeatableInScan,
        inconclusive: false,
        // A stable value whose same-buffer sentinel changed stays hashed: it is what the site
        // repeatedly observes. A value that also failed to repeat has nothing stable to hash.
        hashable: repeatableInScan,
        signalStatus: "readable",
      };
    }
    if (!repeatableInScan) {
      return {
        outcome: "unstable",
        repeatableInScan: false,
        randomizedInScan: true,
        inconclusive: true,
        hashable: false,
        signalStatus: "readable",
      };
    }
    return {
      outcome: "read",
      repeatableInScan: true,
      randomizedInScan: false,
      inconclusive: false,
      hashable: true,
      signalStatus: "readable",
    };
  }
  if (a.status === "blocked" && b.status === "blocked") {
    return {
      outcome: "blocked",
      repeatableInScan: false,
      randomizedInScan: false,
      inconclusive: false,
      hashable: false,
      signalStatus: "blocked",
    };
  }
  if (a.status === "unsupported" && b.status === "unsupported") {
    return {
      outcome: "unavailable",
      repeatableInScan: false,
      randomizedInScan: false,
      inconclusive: false,
      hashable: false,
      signalStatus: "unsupported",
    };
  }
  return {
    outcome: "unstable",
    repeatableInScan: false,
    randomizedInScan: false,
    inconclusive: true,
    hashable: false,
    signalStatus: a.status === "timeout" || b.status === "timeout" ? "timeout" : "error",
  };
}

/**
 * Classify two reads of an exposure-only surface (fonts, voices). These surfaces have no
 * known-value test, so two successful reads that disagree are *instability* — the value
 * changed during the scan — never conclusive evidence of deliberate scrambling. And two
 * matching reads establish only that the site sees a repeatable value: a filtered,
 * standardized or per-site substituted list repeats exactly the same way, so a match must
 * never be presented as the user's true inventory.
 */
export function assessExposureProbes(a: Probe, b: Probe): RepeatedProbeAssessment {
  if (a.status === "readable" && b.status === "readable") {
    const repeatableInScan = a.value === b.value;
    return {
      // Differing exposure reads are inconclusive, unlike the controlled tests where the same
      // observation is direct evidence of a changing return value.
      outcome: repeatableInScan ? "read" : "unstable",
      repeatableInScan,
      randomizedInScan: !repeatableInScan,
      inconclusive: !repeatableInScan,
      hashable: repeatableInScan,
      signalStatus: "readable",
    };
  }
  if (a.status === "blocked" && b.status === "blocked") {
    return {
      outcome: "blocked",
      repeatableInScan: false,
      randomizedInScan: false,
      inconclusive: false,
      hashable: false,
      signalStatus: "blocked",
    };
  }
  if (a.status === "unsupported" && b.status === "unsupported") {
    return {
      outcome: "unavailable",
      repeatableInScan: false,
      randomizedInScan: false,
      inconclusive: false,
      hashable: false,
      signalStatus: "unsupported",
    };
  }
  // Errors, timeouts and mixed status pairs: inconclusive, never hashed, never a defence.
  return {
    outcome: "unstable",
    repeatableInScan: false,
    randomizedInScan: false,
    inconclusive: true,
    hashable: false,
    signalStatus: a.status === "timeout" || b.status === "timeout" ? "timeout" : "error",
  };
}

/** Combined status for sub-probes that agree on an outcome; prefers the most informative
 * failure word when the shared outcome is "unstable". */
function mergedSignalStatus(parts: readonly RepeatedProbeAssessment[]): SignalStatus {
  if (parts.some((p) => p.signalStatus === "timeout")) return "timeout";
  return parts.find((p) => p.signalStatus !== "readable")?.signalStatus ?? "readable";
}

/**
 * Fold several sub-probe assessments into one vector-level assessment, honestly.
 *
 * - A sub-probe whose API this build never shipped ("unavailable") drops out instead of
 *   dragging the vector down: unsupported WebGL 2 must not make a working WebGL 1 vector look
 *   blocked, and a vector is only "unavailable" when *every* sub-probe is.
 * - Modification evidence in any remaining sub-probe ("scrambled") carries to the vector: a
 *   changed known render is evidence however the sibling probe behaved.
 * - Otherwise sub-probes must agree. Mixed conclusive outcomes (one read, one blocked; one
 *   read, one unstable) are reported as "unstable": presenting the successful half alone would
 *   conceal the failed half, and presenting the failure alone would overstate it.
 *
 * Vector-level `hashable` is the all-or-nothing gate the collector applies to every row of
 * the vector: when the fold is inconclusive, every sub-probe stays out of the hash, even one
 * that individually repeated, so the report's "unstable results are excluded" claim holds
 * without fine print.
 */
export function combineVectorAssessments(
  parts: readonly RepeatedProbeAssessment[]
): RepeatedProbeAssessment {
  const present = parts.filter((p) => p.outcome !== "unavailable");
  if (present.length === 0) {
    return {
      outcome: "unavailable",
      repeatableInScan: false,
      randomizedInScan: false,
      inconclusive: false,
      hashable: false,
      signalStatus: "unsupported",
    };
  }
  if (present.length === 1) return present[0];
  if (present.some((p) => p.outcome === "scrambled")) {
    return {
      outcome: "scrambled",
      repeatableInScan: present.every((p) => p.repeatableInScan),
      randomizedInScan: present.some((p) => p.randomizedInScan),
      inconclusive: false,
      hashable: present.every((p) => p.hashable),
      signalStatus: "readable",
    };
  }
  if (present.every((p) => p.outcome === present[0].outcome)) {
    return {
      outcome: present[0].outcome,
      repeatableInScan: present.every((p) => p.repeatableInScan),
      randomizedInScan: present.some((p) => p.randomizedInScan),
      inconclusive: present.some((p) => p.inconclusive),
      hashable: present.every((p) => p.hashable),
      signalStatus: mergedSignalStatus(present),
    };
  }
  return {
    outcome: "unstable",
    repeatableInScan: false,
    randomizedInScan: present.some((p) => p.randomizedInScan),
    inconclusive: true,
    hashable: false,
    signalStatus: present.some((p) => p.signalStatus === "timeout") ? "timeout" : "error",
  };
}

/** Bumped whenever the hash inputs or their encoding change, so an ID from an older build is
 * never mistaken for a mismatch against a newer one. Hash inputs are keyed by these stable
 * internal keys rather than by display labels — editing copy must not change anyone's ID.
 * 8.0.0: hash only successful values, use same-buffer sentinels, and classify repeat outcomes
 * explicitly. Canvas, WebGL and audio enter the hash only when two independent reads match.
 * 9.0.0: fonts and default font metrics enter the hash only when two fresh reads match; audio
 * device metadata (sample rate, output channels) leaves the hash because it follows the active
 * output device and is never verified by the repeated OfflineAudioContext probe; a WebGL 2
 * shader readback joins the hash when two fresh contexts return identical pixels; and hash
 * inclusion is gated by the whole vector's folded assessment — when a vector is inconclusive
 * (one sub-probe blocked or unstable), every sub-probe of that vector stays out of the hash,
 * even one that individually repeated. */
export const FINGERPRINT_ALGORITHM_VERSION = "9.0.0";

export interface FpSignal {
  category: string;
  label: string;
  /** Stable identifier for this signal, independent of the display label. */
  key: string;
  value: string;
  status: SignalStatus;
  /** True when this signal's raw value is part of the hashed input. */
  hashed: boolean;
  /** Optional annotation, e.g. evidence that the browser altered this value. */
  note?: string;
  /** What kind of claim the note makes, as data. "modification" is reserved for changed
   * known-value evidence; "observation" is a neutral remark (differing reads, exclusions).
   * The UI keys its tone off this field — never off the note's wording — so a neutral
   * observation can never be re-coloured into defence evidence by a presentation layer. */
  noteKind?: "modification" | "observation";
}

export interface ConsistencyCheck {
  label: string;
  /** `skipped` means the inputs this check needs weren't available, so it never ran.
   * Skipped checks must never be reported as passing. */
  status: "ok" | "warn" | "bad" | "skipped";
  detail: string;
  /** True for checks that compare two sources that must agree. A failure here is a genuine
   * self-contradiction. Non-comparison checks (an exposed automation flag, an implausible
   * screen) are suspicious without contradicting anything, so they must not drive a
   * "contradicts itself" verdict. */
  comparison: boolean;
}

/** One of the five strong fingerprinting vectors, with what actually happened to it. */
export interface VectorState {
  key: "canvas" | "webgl" | "audio" | "fonts" | "voices";
  label: string;
  status: SignalStatus;
  /** User-facing result derived from all required reads, not merely the first one. */
  outcome: VectorOutcome;
  /** True when two successful reads in the same scan disagreed. This is evidence of a changing
   * return value, but it cannot identify the browser feature or extension responsible. */
  randomizedInScan: boolean;
  /** Two reads disagreed on *status* (one worked, one didn't). Unstable, so excluded from the
   * hash — but not counted as a privacy defence, because a flaky read isn't protection. */
  inconclusive: boolean;
  /** True when two independent reads succeeded with the same value. Measured for every vector;
   * it does not prove that the returned value is original. */
  repeatableInScan?: boolean;
  /** A changed known test value from a controlled test (canvas, WebGL, audio). Exposure-only
   * vectors never set this: they have no known value to check against. */
  tamperEvidence?: string;
  /** True for vectors with no ground-truth known-value test, whose failed reads must NEVER be
   * presented as a defence and whose in-scan disagreement is instability, not scrambling.
   * Fonts and voices only expose values a browser may filter, standardize or substitute. */
  exposureOnly?: boolean;
  /** Raw value, so the caller can compare it against the previous run and catch
   * per-session randomisation that a single scan cannot see. */
  raw: string;
}

export interface FingerprintMeta {
  signalCount: number;
  /** Signals that go into the hash (the rest are shown but excluded as volatile). */
  hashedCount: number;
  /** Per-vector counts — one dead collector counts once, not once per row it fills. */
  vectorsRead: number;
  /** Vectors that returned a value, including values with scrambling evidence. */
  vectorsReadable: number;
  vectorsBlocked: number;
  vectorsUnavailable: number;
  vectorsScrambled: number;
  vectorsUnstable: number;
  vectorsTotal: number;
  /** Rows whose read was blocked, failed, or timed out (not APIs the browser never shipped). */
  failedRows: number;
  /** Wall-clock collection time, so the UI never has to guess at "about a second". */
  durationMs: number;
  /** Which hashing algorithm produced `hash` — IDs are only comparable within one version. */
  algorithmVersion: string;
}

export interface FingerprintReport {
  hash: string;
  signals: FpSignal[];
  consistency: ConsistencyCheck[];
  vectors: VectorState[];
  meta: FingerprintMeta;
  /** Exact, ordered input to SHA-256. Included so downloads are independently verifiable. */
  hashInput: {
    v: string;
    components: [key: string, value: string][];
  };
}

/** Canonical, delimiter-safe encoding used by the snapshot hash. Exported for focused tests
 * and third-party verification of downloaded reports. */
export function canonicalFingerprintInput(
  pairs: readonly (readonly [key: string, value: string])[],
  version = FINGERPRINT_ALGORITHM_VERSION
): string {
  const seen = new Set<string>();
  const components = pairs.map(([key, value]) => {
    if (seen.has(key)) throw new Error(`fingerprint: duplicate canonical key "${key}".`);
    seen.add(key);
    return [key, value] as [string, string];
  });
  components.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return JSON.stringify({ v: version, components });
}

/** Resolve `p` normally, or `fallback` if it doesn't settle within `ms` — so a blocked browser
 * API (e.g. a stalled OfflineAudioContext render) can never hang fingerprint collection. */
function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    p.catch(() => fallback),
    new Promise<T>((resolve) => {
      timer = setTimeout(() => resolve(fallback), ms);
    }),
    // Clear the losing timer as soon as the race settles, rather than leaving one pending
    // per probe per scan until its deadline elapses.
  ]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

/**
 * Frozen label → hash-key registry. The hash is keyed by these, never by the display label,
 * so rewording UI copy cannot change anyone's snapshot. Renaming a label deliberately breaks
 * the lookup and throws in development, forcing an explicit decision: keep the old key (copy
 * change, ID unchanged) or mint a new one and bump FINGERPRINT_ALGORITHM_VERSION.
 *
 * Never edit an existing value here. Add new entries freely.
 */
const SIGNAL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  // Browser & platform
  "User agent": "user_agent",
  "Navigator platform": "navigator_platform",
  Cookies: "cookies_enabled",
  "Do Not Track": "do_not_track",
  Automation: "webdriver",
  "PDF viewer": "pdf_viewer",
  // Client Hints
  "Client Hints": "client_hints_support",
  Brands: "ch_brands",
  Mobile: "ch_mobile",
  "Client Hints platform": "ch_platform",
  "Platform version": "ch_platform_version",
  Architecture: "ch_architecture",
  Bitness: "ch_bitness",
  "Full browser version": "ch_full_version",
  "Full version list": "ch_full_version_list",
  Model: "ch_model",
  WOW64: "ch_wow64",
  "Form factors": "ch_form_factors",
  // Language & time
  Languages: "languages",
  Timezone: "timezone",
  "Timezone offset": "timezone_offset",
  // Screen & display
  Screen: "screen",
  "Color scheme": "prefers_color_scheme",
  "Reduced motion": "prefers_reduced_motion",
  "Color gamut": "color_gamut",
  HDR: "dynamic_range",
  // Input & CSS
  Pointer: "pointer",
  Hover: "hover",
  "Forced colors": "forced_colors",
  "Contrast preference": "prefers_contrast",
  // Hardware & network
  "CPU threads": "hardware_concurrency",
  "Device memory": "device_memory",
  "Touch points": "max_touch_points",
  Network: "network",
  // Storage
  LocalStorage: "local_storage",
  SessionStorage: "session_storage",
  IndexedDB: "indexed_db",
  "Cache API": "cache_api",
  "Storage quota": "storage_quota",
  // API support
  WebGPU: "api_webgpu",
  WebUSB: "api_webusb",
  WebHID: "api_webhid",
  "Web Bluetooth": "api_web_bluetooth",
  "Web Serial": "api_web_serial",
  WebRTC: "api_webrtc",
  // Permissions
  Notifications: "perm_notifications",
  Geolocation: "perm_geolocation",
  Camera: "perm_camera",
  Microphone: "perm_microphone",
  "Clipboard read": "perm_clipboard_read",
  MIDI: "perm_midi",
  // Canvas / WebGL
  Canvas: "canvas",
  "Canvas details": "canvas_details",
  "WebGL vendor": "webgl_vendor",
  "WebGL renderer": "webgl_renderer",
  "Max texture size": "webgl_max_texture_size",
  "Max viewport": "webgl_max_viewport",
  "Max renderbuffer size": "webgl_max_renderbuffer",
  "Vertex limits": "webgl_vertex_limits",
  "Combined texture units": "webgl_combined_texture_units",
  "Shader precision": "webgl_shader_precision",
  // Audio
  Audio: "audio",
  "Sample rate": "audio_sample_rate",
  "Output channels": "audio_output_channels",
  OfflineAudioContext: "offline_audio_context",
  // Maths / blocking / frame
  "Float results": "math_float_results",
  "Functions tested": "math_functions_tested",
  "Ad-style elements": "dom_blocker",
  "Usable area": "screen_usable_area",
  "WebGL extensions": "webgl_extensions",
  "WebGL readback": "webgl_readback",
  "WebGL 2 shader": "webgl2_shader",
  // Fonts & media
  Fonts: "fonts",
  "Default font metrics": "font_preferences",
  "Speech voices": "speech_voices",
  "Video codecs": "video_codecs",
  // Media devices
  Cameras: "device_cameras",
  Microphones: "device_microphones",
  Speakers: "device_speakers",
  "Media devices": "media_devices",
  // Power
  "Battery level": "battery_level",
  Charging: "battery_charging",
  "Time to full": "battery_time_to_full",
  "Time to empty": "battery_time_to_empty",
  Battery: "battery",
});

export async function sha256hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// --- User-Agent Client Hints (navigator.userAgentData) ---------------------------------
// Chromium's structured replacement for the User-Agent string. Not yet in TypeScript's DOM
// lib and not implemented by Firefox/Safari, hence the local types and the optional access.

interface UABrand {
  brand: string;
  version: string;
}

interface UADataValues {
  architecture?: string;
  bitness?: string;
  platformVersion?: string;
  uaFullVersion?: string;
  fullVersionList?: UABrand[];
  model?: string;
  wow64?: boolean;
  formFactors?: string[];
}

interface NavigatorUAData {
  brands: UABrand[];
  mobile: boolean;
  platform: string;
  getHighEntropyValues(hints: string[]): Promise<UADataValues>;
}

const HIGH_ENTROPY_HINTS = [
  "architecture",
  "bitness",
  "platformVersion",
  "uaFullVersion",
  "fullVersionList",
  "model",
  "wow64",
  "formFactors",
];

function formatBrands(brands?: UABrand[]): string {
  if (!brands?.length) return "";
  return brands.map((b) => `${b.brand} ${b.version}`).join(" · ");
}

/** First non-GREASE brand ("Not)A;Brand" etc. is deliberate noise, not a real browser). */
function realBrand(brands?: UABrand[]): UABrand | undefined {
  return brands?.find((b) => !/not.a.brand/i.test(b.brand));
}

// --- Canvas / WebGL / Audio -------------------------------------------------------------

const CANVAS_W = 288;
const CANVAS_H = 60;

/** A single read: what came back, and why. */
export interface Probe {
  status: SignalStatus;
  /** Raw value for hashing and cross-run comparison; empty unless readable. */
  value: string;
  /** Evidence that a known test value came back changed, separate from repeatability. */
  tamperEvidence?: string;
}

/** Outcome of a known-value sentinel check. `clean` means only that the sentinel itself matched;
 * it cannot certify unknown fingerprint-bearing pixels or samples elsewhere in the buffer.
 * `refused` is separate from `altered` on purpose: a read that returned nothing at all was
 * blocked, and reporting that as changed data would misdescribe what happened. */
export interface TamperProbe {
  status: "clean" | "altered" | "refused" | "unavailable";
  /** Human-readable proof, set only when status is "altered" or "refused". */
  evidence?: string;
}

/** A sentinel extraction that returned without writing is an observed refusal of that read.
 * Keep this mapping pure so the WebGL blocking path has focused regression coverage. */
export function statusAfterVerification(
  status: SignalStatus,
  verification: TamperProbe
): SignalStatus {
  return verification.status === "refused" ? "blocked" : status;
}

const TAMPER_UNAVAILABLE: TamperProbe = { status: "unavailable" };

interface CheckedProbe extends Probe {
  /** Sentinel check from the same returned buffer and extraction path stored in `value`. */
  verification: TamperProbe;
}

const CHECKED_TIMEOUT: CheckedProbe = {
  status: "timeout",
  value: "",
  verification: TAMPER_UNAVAILABLE,
};

/**
 * Known-value sentinel, painted on every odd row of the same canvas that gets hashed and
 * checked out of the same ImageData object.
 *
 * Two properties matter, and both are load-bearing:
 *
 * 1. **Interleaved.** Every second row is checked, so half the buffer is ground truth. This is
 *    more sensitive to modifications distributed across the image than a small corner panel,
 *    but no probability guarantee is made about a browser or extension's current algorithm.
 *
 * 2. **Non-uniform along x.** Each sentinel row is a per-pixel ramp, not a solid scanline.
 *    WebKit's noise (`CanvasNoiseInjection::postProcessPixelBufferResults`) clamps each pixel's
 *    offset to the range spanned by its tightest bounding neighbour pair, and collapses that
 *    range to zero as soon as one pair is uniform. A non-uniform ramp therefore exercises more
 *    of that path than a solid row. This is a sensitivity improvement, not a completeness claim.
 *
 * The ramp steps by one grey level per pixel and stays inside 40..103, so consecutive pixels
 * bracket each other in every channel and neither endpoint saturates. Values are opaque and
 * painted with integer-aligned `fillRect`, which is exact in an 8-bit sRGB readback: no
 * antialiasing, no alpha compositing, no colour conversion. `putImageData` would be cheaper but
 * is deliberately not used — WebKit only adds regions touched by *drawing commands* to its
 * noise dirty rect, so a pattern written that way would report clean on Safari.
 *
 * None of this makes detection complete: a stable substitution, a patch that reproduces this
 * exact ramp, or changes confined to the even rows all still pass.
 */

/** Every Nth row is a sentinel. 2 = half the buffer checked. */
const SENTINEL_ROW_STEP = 2;

/** Grey level for one sentinel pixel. The `3 * y` phase shift keeps the vertical and diagonal
 * neighbour pairs non-uniform too, so no neighbour pair can collapse the clamp range. */
function sentinelGrey(x: number, y: number): number {
  return 40 + ((x + 3 * y) % 64);
}

/** Encode bytes without depending on `toDataURL`. Chunking avoids the argument limit of a
 * single `String.fromCharCode(...bytes)` call on the full canvas. */
function bytesToBase64(bytes: Uint8Array | Uint8ClampedArray): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

/** Draw, check, and fingerprint one canvas through one `getImageData` call. The previous
 * implementation certified a separate 48×48 canvas through `getImageData` but hashed this
 * artwork through `toDataURL`; a deterministic toDataURL-only patch therefore bypassed the
 * check. Keeping the known-colour panel in the same buffer makes the hint more relevant, but
 * does not prove that the separate fingerprint artwork was untouched. */
function canvasFp(): CheckedProbe {
  let c: HTMLCanvasElement | undefined;
  try {
    c = document.createElement("canvas");
    c.width = CANVAS_W;
    c.height = CANVAS_H;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx) return { status: "unsupported", value: "", verification: TAMPER_UNAVAILABLE };
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("Proxidize fingerprint 🦄", 2, 15);
    ctx.fillStyle = "rgba(102,204,0,0.7)";
    ctx.fillText("Proxidize fingerprint 🦄", 4, 17);

    // Sentinel rows go on top of the artwork, so checked and unchecked pixels interleave across
    // the whole surface instead of sitting in a corner a noise pass can miss. Drawn a pixel at a
    // time on purpose: see `sentinelGrey`.
    for (let y = 1; y < CANVAS_H; y += SENTINEL_ROW_STEP) {
      for (let x = 0; x < CANVAS_W; x++) {
        const v = sentinelGrey(x, y);
        ctx.fillStyle = `rgb(${v},${v},${v})`;
        ctx.fillRect(x, y, 1, 1);
      }
    }

    // One extraction feeds both the check and the hash, so no protection can alter the hashed
    // bytes through a path the check never saw.
    const { data } = ctx.getImageData(0, 0, CANVAS_W, CANVAS_H);
    let altered = 0;
    let checked = 0;
    for (let y = 1; y < CANVAS_H; y += SENTINEL_ROW_STEP) {
      for (let x = 0; x < CANVAS_W; x++) {
        const v = sentinelGrey(x, y);
        const i = (y * CANVAS_W + x) * 4;
        checked++;
        if (data[i] !== v || data[i + 1] !== v || data[i + 2] !== v || data[i + 3] !== 255) {
          altered++;
        }
      }
    }
    const verification: TamperProbe =
      altered > 0
        ? {
            status: "altered",
            evidence: `${altered} of ${checked} test pixels did not match the colours we drew`,
          }
        : { status: "clean" };
    return {
      status: "readable",
      value: bytesToBase64(data),
      verification,
      tamperEvidence: verification.evidence,
    };
  } catch (error) {
    return { status: statusFromException(error), value: "", verification: TAMPER_UNAVAILABLE };
  } finally {
    // Drop the backing store promptly; repeated recomputes should not retain large pixel buffers.
    if (c) {
      c.width = 0;
      c.height = 0;
    }
  }
}

interface WebGlInfo {
  vendor: string;
  renderer: string;
  maxTexture: string;
  maxViewport: string;
  maxRenderbuffer: string;
  vertexAttribs: string;
  vertexTextureUnits: string;
  varyingVectors: string;
  combinedTextureUnits: string;
  shaderPrecision: string;
  /** Supported extension list — varies by GPU, driver and browser build. */
  extensions: string;
}

/** Omit an unavailable sub-field instead of hashing an empty or display-only placeholder. */
function webglInfoValue(info: WebGlInfo): string {
  return JSON.stringify(
    Object.fromEntries(Object.entries(info).filter(([, value]) => value !== ""))
  );
}

const WEBGL_UNAVAILABLE = (v: string): WebGlInfo => ({
  vendor: v,
  renderer: v,
  maxTexture: v,
  maxViewport: v,
  maxRenderbuffer: v,
  vertexAttribs: v,
  vertexTextureUnits: v,
  varyingVectors: v,
  combinedTextureUnits: v,
  shaderPrecision: v,
  extensions: v,
});

/** WebGL fills ten display rows from one context. The status travels alongside so a single
 * dead context is counted as one failed vector, not ten failed reads. */
interface WebGlProbe {
  status: SignalStatus;
  info: WebGlInfo;
  readback: string;
  verification: TamperProbe;
  /** The controlled-render read was attempted and went wrong; see webglReadback. */
  readbackFailed: boolean;
}

/** Effective status of one WebGL probe for assessment purposes, pure for focused tests.
 * A refused readback is an observed block; a failed readback (GL error, context loss,
 * exception) degrades the probe to an error, so a scan whose controlled render never
 * succeeded can never present its capability profile as a verified, hashable read. Hardware
 * where the comparison can't exist (sub-8-bit framebuffer) is not a failure and passes
 * through, since that limitation is deterministic and the capability reads stand on their own. */
export function webglProbeStatus(
  status: SignalStatus,
  verification: TamperProbe,
  readbackFailed: boolean
): SignalStatus {
  if (status !== "readable") return status;
  if (verification.status === "refused") return "blocked";
  if (readbackFailed) return "error";
  return status;
}

/**
 * Ground-truth WebGL test, same principle as the canvas one. Clear the left half to one colour
 * and the right half to another (scissored clears are pixel-exact — no rasterisation, no
 * antialiasing), then compare every pixel of the readback against the colour drawn there.
 *
 * Two colours and a full expected-value comparison catch a uniformly rewritten buffer that a
 * distinct-colour count would miss. Black and white use exactly representable clear values; a
 * framebuffer with fewer than eight RGB bits reports the check as unavailable rather than being
 * compared against 8-bit expectations. Matching proves only this readback—not other WebGL data.
 */
/** Neither clear colour (0 or 255) nor the 0 a fresh buffer would hold, so an untouched
 * destination can only mean the read wrote nothing. Exported for the pure-verifier tests. */
export const WEBGL_READBACK_FILL = 0x7f;

/** Side length of the WebGL controlled render targets, in pixels. */
const GL_TEST_SIZE = 16;

/**
 * Pure verifier for the WebGL controlled renders: expects a 16×16 RGBA8 buffer whose left
 * eight columns are opaque black and right eight columns opaque white. Isolated from any GL
 * context so the truth table has unit coverage without a GPU.
 *
 * - A buffer still holding only the sentinel prefill byte means readPixels returned without
 *   writing: an observed refusal of that read, not changed data.
 * - A wrong-length buffer cannot be judged and reports unavailable.
 * - Any pixel differing from the drawn pattern is modification evidence about this test render
 *   only; a clean result certifies nothing beyond these 256 pixels.
 */
export function verifyShaderReadback(px: Uint8Array | Uint8ClampedArray): TamperProbe {
  if (px.length !== GL_TEST_SIZE * GL_TEST_SIZE * 4) return TAMPER_UNAVAILABLE;
  if (px.every((byte) => byte === WEBGL_READBACK_FILL)) {
    return {
      status: "refused",
      evidence: "readPixels returned without writing any pixels",
    };
  }
  let altered = 0;
  for (let i = 0; i < px.length; i += 4) {
    const v = (i / 4) % GL_TEST_SIZE < GL_TEST_SIZE / 2 ? 0 : 255;
    if (px[i] !== v || px[i + 1] !== v || px[i + 2] !== v || px[i + 3] !== 255) altered++;
  }
  return altered > 0
    ? {
        status: "altered",
        evidence: `${altered} of ${GL_TEST_SIZE * GL_TEST_SIZE} pixels came back a different colour than was drawn`,
      }
    : { status: "clean" };
}

const GL2_VERTEX_SHADER = `#version 300 es
in vec2 p;
void main() {
  gl_Position = vec4(p, 0.0, 1.0);
}`;

/** step() at an exact half-texel boundary: every fragment centre sits at x.5, so no fragment
 * ever lands on the threshold and the output is pixel-exact black or white with no rounding. */
const GL2_FRAGMENT_SHADER = `#version 300 es
precision mediump float;
out vec4 c;
void main() {
  float w = step(8.0, gl_FragCoord.x);
  c = vec4(w, w, w, 1.0);
}`;

/**
 * Controlled WebGL 2 test that exercises the *programmable* pipeline, unlike the WebGL 1 probe
 * whose scissored clears never run a shader. A fullscreen triangle is drawn through a real
 * vertex + fragment shader into a known-size RGBA8 texture (an explicit render target, so the
 * drawing buffer's format can't change what "8-bit" means), then every pixel of the readback
 * is compared against the deterministic pattern the shader computes.
 *
 * Dithering is disabled and blending/depth/scissor are off, so no legitimate implementation
 * variation can look like injected noise. Compile and link status are checked so an
 * implementation fault reads as an error, never as evidence. A changed pixel is modification
 * evidence about this render only; a matching one proves nothing about whether the separate
 * renderer/capability strings are truthful. WebGL 2 being absent is a deterministic
 * "unsupported", which the vector aggregation drops rather than counting as a block.
 */
function webgl2ShaderFp(): CheckedProbe {
  let gl: WebGL2RenderingContext | null = null;
  const fail = (status: SignalStatus): CheckedProbe => ({
    status,
    value: "",
    verification: TAMPER_UNAVAILABLE,
  });
  try {
    const canvas = document.createElement("canvas");
    canvas.width = GL_TEST_SIZE;
    canvas.height = GL_TEST_SIZE;
    gl = canvas.getContext("webgl2", {
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
    });
    if (!gl) return fail("unsupported");
    // Non-null alias, so the compile closure below needs no assertions.
    const ctx = gl;

    const compile = (type: number, source: string): WebGLShader | null => {
      const shader = ctx.createShader(type);
      if (!shader) return null;
      ctx.shaderSource(shader, source);
      ctx.compileShader(shader);
      return ctx.getShaderParameter(shader, ctx.COMPILE_STATUS) ? shader : null;
    };
    const vs = compile(gl.VERTEX_SHADER, GL2_VERTEX_SHADER);
    const fs = compile(gl.FRAGMENT_SHADER, GL2_FRAGMENT_SHADER);
    if (!vs || !fs) return fail("error");
    const program = gl.createProgram();
    if (!program) return fail("error");
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return fail("error");

    // Explicit RGBA8 render target, so the comparison never depends on drawing-buffer format.
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, GL_TEST_SIZE, GL_TEST_SIZE);
    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      return fail("error");
    }

    // Every source of legitimate per-pixel variation off, for the same reason as the WebGL 1
    // probe: dithering may legally vary by coordinate, which is indistinguishable from noise.
    gl.disable(gl.DITHER);
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.SCISSOR_TEST);
    gl.viewport(0, 0, GL_TEST_SIZE, GL_TEST_SIZE);

    // One fullscreen triangle covers every pixel of the target by construction.
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.useProgram(program);
    const loc = gl.getAttribLocation(program, "p");
    if (loc < 0) return fail("error");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    // Sentinel-prefilled destination: a readPixels that writes nothing stays distinguishable
    // from changed data, exactly as in the WebGL 1 readback.
    const px = new Uint8Array(GL_TEST_SIZE * GL_TEST_SIZE * 4).fill(WEBGL_READBACK_FILL);
    gl.readPixels(0, 0, GL_TEST_SIZE, GL_TEST_SIZE, gl.RGBA, gl.UNSIGNED_BYTE, px);
    // A GL error or a lost context makes the readback meaningless, not suspicious.
    if (gl.getError() !== gl.NO_ERROR || gl.isContextLost()) return fail("error");

    const verification = verifyShaderReadback(px);
    return {
      status: "readable",
      value: bytesToBase64(px),
      verification,
      tamperEvidence: verification.status === "altered" ? verification.evidence : undefined,
    };
  } catch (error) {
    return fail(statusFromException(error));
  } finally {
    // Explicitly lose the context so repeated scans don't accumulate live GPU contexts.
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  }
}

/** Exported for direct fake-context tests of the failure/limitation branches below. */
export function webglReadback(gl: WebGLRenderingContext): {
  value: string;
  verification: TamperProbe;
  /** True when the controlled render was attempted and went wrong (GL error, context loss,
   * exception, or a drawing buffer that came back a different size than requested) — distinct
   * from `verification: unavailable` on hardware where the 8-bit comparison can't exist. A
   * failed read must degrade the whole probe: without it, two capability profiles with empty
   * readbacks could compare equal and hash while no render was ever verified. */
  failed: boolean;
} {
  try {
    // Ordering is load-bearing. A lost context returns null from ordinary queries per the
    // WebGL spec, and Number(null) is 0 — so checking bit depth first misread a lost context
    // as "sub-8-bit hardware" and let unverified capability profiles stay readable. Reject
    // the failure modes first; only a genuine numeric answer below 8 is the permitted
    // hardware limitation.
    if (gl.isContextLost()) {
      return { value: "", verification: TAMPER_UNAVAILABLE, failed: true };
    }
    // The spec allows an implementation to hand back a smaller drawing buffer than requested
    // (typically under resource pressure). The render check can't run, so the probe must not
    // stay readable: a "failed: false" here let capability profiles hash with no verified
    // render on a standards-permitted path.
    if (gl.drawingBufferWidth !== 16 || gl.drawingBufferHeight !== 16) {
      return { value: "", verification: TAMPER_UNAVAILABLE, failed: true };
    }
    const bits = [
      gl.getParameter(gl.RED_BITS),
      gl.getParameter(gl.GREEN_BITS),
      gl.getParameter(gl.BLUE_BITS),
    ];
    // null or any non-numeric answer means the query itself failed (a context lost mid-race,
    // a GL error state), never small hardware — no coercion, so it can't read as "< 8".
    if (bits.some((b) => typeof b !== "number" || !Number.isFinite(b))) {
      return { value: "", verification: TAMPER_UNAVAILABLE, failed: true };
    }
    // The one true hardware limitation: on a sub-8-bit framebuffer the exact-value comparison
    // cannot exist, deterministically, so the capability reads stand on their own.
    if (bits.some((b) => (b as number) < 8)) {
      return { value: "", verification: TAMPER_UNAVAILABLE, failed: false };
    }
    // DITHER is enabled by default in GLES and dithering is explicitly allowed to vary by pixel
    // coordinate, which would look exactly like injected noise. Turn it off, or a clean GPU gets
    // reported as protected — the same false alarm competitors ship.
    gl.disable(gl.DITHER);
    const left: [number, number, number] = [0, 0, 0];
    const right: [number, number, number] = [255, 255, 255];
    gl.clearColor(left[0] / 255, left[1] / 255, left[2] / 255, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(8, 0, 8, 16);
    gl.clearColor(right[0] / 255, right[1] / 255, right[2] / 255, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.disable(gl.SCISSOR_TEST);
    // Pre-filled with a byte that appears in neither clear colour nor in a zeroed buffer, so a
    // readPixels that returns without writing anything is distinguishable from changed data.
    // A plain zero-filled destination would incorrectly describe such a refusal as 256 altered
    // pixels, because black is also one of the expected clear colours.
    const px = new Uint8Array(16 * 16 * 4).fill(WEBGL_READBACK_FILL);
    gl.readPixels(0, 0, 16, 16, gl.RGBA, gl.UNSIGNED_BYTE, px);
    // A GL error or a lost context makes the readback meaningless, not suspicious — but it is
    // a *failed* read, so the probe as a whole must not stay "readable".
    if (gl.getError() !== gl.NO_ERROR || gl.isContextLost()) {
      return { value: "", verification: TAMPER_UNAVAILABLE, failed: true };
    }
    // Same left-black/right-white 16×16 pattern as the WebGL 2 shader test, so both share one
    // unit-tested pure verifier — including the untouched-sentinel "refused" detection.
    const verification = verifyShaderReadback(px);
    if (verification.status === "refused") {
      return { value: "", verification, failed: false };
    }
    // These exact bytes are also a hash component when both contexts return the same value.
    return { value: bytesToBase64(px), verification, failed: false };
  } catch {
    return { value: "", verification: TAMPER_UNAVAILABLE, failed: true };
  }
}

function webglInfo(): WebGlProbe {
  const wrap = (
    status: SignalStatus,
    info: WebGlInfo,
    readback = "",
    verification = TAMPER_UNAVAILABLE,
    readbackFailed = false
  ): WebGlProbe => ({ status, info, readback, verification, readbackFailed });
  let gl: WebGLRenderingContext | null = null;
  try {
    const c = document.createElement("canvas");
    c.width = 16;
    c.height = 16;
    gl = (c.getContext("webgl", { antialias: false, preserveDrawingBuffer: true }) ||
      c.getContext("experimental-webgl", {
        antialias: false,
        preserveDrawingBuffer: true,
      })) as WebGLRenderingContext | null;
    if (!gl) return wrap("unsupported", WEBGL_UNAVAILABLE("unsupported"));
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
    const renderer = dbg
      ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);
    const viewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array | null;
    const prec = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
    const info: WebGlInfo = {
      vendor: vendor == null ? "" : String(vendor),
      renderer: renderer == null ? "" : String(renderer),
      maxTexture: String(gl.getParameter(gl.MAX_TEXTURE_SIZE) ?? ""),
      maxViewport: viewport ? `${viewport[0]}/${viewport[1]}` : "",
      maxRenderbuffer: String(gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) ?? ""),
      vertexAttribs: String(gl.getParameter(gl.MAX_VERTEX_ATTRIBS) ?? ""),
      vertexTextureUnits: String(gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS) ?? ""),
      varyingVectors: String(gl.getParameter(gl.MAX_VARYING_VECTORS) ?? ""),
      combinedTextureUnits: String(gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS) ?? ""),
      shaderPrecision: prec ? `${prec.precision}/${prec.rangeMin}/${prec.rangeMax}` : "",
      extensions: JSON.stringify((gl.getSupportedExtensions() ?? []).slice().sort()),
    };
    const readback = webglReadback(gl);
    return wrap("readable", info, readback.value, readback.verification, readback.failed);
  } catch (error) {
    const status = statusFromException(error);
    return wrap(status, WEBGL_UNAVAILABLE(status));
  } finally {
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  }
}

/**
 * The same identity-bearing values as seen from inside a Web Worker. Anti-detect browsers and
 * spoofing extensions almost always patch the main thread and forget worker scope, so a mismatch
 * is strong evidence consistent with partial spoofing — reported as a warning, not proof, since
 * a browser that farbles each context separately looks the same. Unlike our double-read tests it
 * works on plain scalars (CPU threads, timezone, platform) that read identically however faked.
 */
interface WorkerSnapshot {
  userAgent: string;
  hardwareConcurrency: number;
  platform: string;
  timezone: string;
  languages: string;
}

function workerSnapshot(): Promise<WorkerSnapshot | null> {
  return new Promise((resolve) => {
    if (typeof Worker === "undefined") return resolve(null);
    const code = `self.onmessage=function(){try{postMessage({userAgent:navigator.userAgent,hardwareConcurrency:navigator.hardwareConcurrency,platform:navigator.platform,timezone:Intl.DateTimeFormat().resolvedOptions().timeZone,languages:(navigator.languages||[]).join(", ")})}catch(e){postMessage(null)}}`;
    let url: string | undefined;
    let worker: Worker | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const done = (value: WorkerSnapshot | null) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      try {
        worker?.terminate();
      } catch {
        // Already gone.
      }
      // Revoked on every exit path, including a constructor that throws.
      if (url) URL.revokeObjectURL(url);
      resolve(value);
    };
    try {
      url = URL.createObjectURL(new Blob([code], { type: "text/javascript" }));
      worker = new Worker(url);
      worker.onmessage = (event: MessageEvent<WorkerSnapshot | null>) => done(event.data);
      // A blocked blob: worker (strict CSP) is not evidence of anything, just unavailable.
      worker.onerror = () => done(null);
      worker.postMessage(1);
      timer = setTimeout(() => done(null), 1500);
    } catch {
      done(null);
    }
  });
}

/** True if the media query matches in this browser. */
function mq(query: string): boolean {
  return typeof matchMedia !== "undefined" && matchMedia(query).matches;
}

function colorGamut(): string {
  if (mq("(color-gamut: rec2020)")) return "rec2020";
  if (mq("(color-gamut: p3)")) return "p3";
  if (mq("(color-gamut: srgb)")) return "srgb";
  return "";
}

function pointerType(): string {
  if (mq("(pointer: fine)")) return "fine";
  if (mq("(pointer: coarse)")) return "coarse";
  if (mq("(pointer: none)")) return "none";
  return "";
}

function contrastPreference(): string {
  if (mq("(prefers-contrast: more)")) return "more";
  if (mq("(prefers-contrast: less)")) return "less";
  if (mq("(prefers-contrast: custom)")) return "custom";
  return "no-preference";
}

function codecsFp(): Probe {
  try {
    const v = document.createElement("video");
    const types: [string, string][] = [
      ["H.264", 'video/mp4; codecs="avc1.42E01E"'],
      ["VP9", 'video/webm; codecs="vp9"'],
      ["AV1", 'video/mp4; codecs="av01.0.05M.08"'],
      ["Ogg", 'video/ogg; codecs="theora"'],
    ];
    const ok = types.filter(([, t]) => v.canPlayType(t) !== "").map(([n]) => n);
    // An empty list is a real answer ("this build plays none of them"), not a failure.
    return { status: "readable", value: JSON.stringify(ok) };
  } catch (error) {
    return { status: statusFromException(error), value: "" };
  }
}

/** The identity-bearing fields of one speech-synthesis voice. */
export interface VoiceRecord {
  name: string;
  lang: string;
  voiceURI: string;
  default: boolean;
  localService: boolean;
}

/**
 * Deterministic canonical encoding of a voice inventory, sorted on the complete serialized
 * row — so two reads that return the same voices in a different order compare equal, and an
 * order-only change can never be reported as instability. The full row is the sort key on
 * purpose: the spec does not guarantee unique names or URIs, so records that tie on every
 * string field but differ in `default`/`localService` still need a total order.
 */
export function canonicalizeVoices(voices: readonly VoiceRecord[]): string {
  const rows = voices
    .map((v) => ({
      name: String(v.name),
      lang: String(v.lang),
      voiceURI: String(v.voiceURI),
      default: Boolean(v.default),
      localService: Boolean(v.localService),
    }))
    .map((row) => ({ row, key: JSON.stringify(row) }))
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
    .map((entry) => entry.row);
  return JSON.stringify(rows);
}

const VOICES_LOAD_DEADLINE_MS = 1500;
const VOICES_POLL_MS = 200;
/** The second read runs on a later task so it exercises a genuinely fresh getVoices() call
 * with a real window for the inventory to change in. */
const VOICES_SECOND_READ_DELAY_MS = 150;

/**
 * Installed speech-synthesis voices — a strong fingerprinting vector, read twice. The list
 * loads asynchronously (`voiceschanged` plus a bounded poll cover engines that fire the event
 * late or never), and per the Web Speech specification an empty list can mean either "no
 * voices" or "not known yet" — so an empty list is never a readable answer, whether it appears
 * at the deadline or as a race after a non-empty observation. Once a non-empty list is
 * observed, two fresh normalized reads are compared; the caller classifies them with
 * {@link assessExposureProbes}, so a changed inventory is reported as instability rather than
 * scrambling. The spec does not promise that the first non-empty list is final — an engine may
 * add remote or OS voices later — which is one more reason this vector is displayed but never
 * hashed. Every timer and listener is cleaned up on every exit path, settling exactly once
 * through `settle`. Exported for the fake-timer orchestration tests.
 */
export function speechVoicesProbes(): Promise<[Probe, Probe]> {
  return new Promise((resolve) => {
    const readCanonical = (): Probe => {
      try {
        const list: VoiceRecord[] = speechSynthesis.getVoices().map((v) => ({
          name: v.name,
          lang: v.lang,
          voiceURI: v.voiceURI,
          default: v.default,
          localService: v.localService,
        }));
        // An empty read is "not known yet", even right after a non-empty observation: the
        // list can legally flap while the engine populates it. Two empty "readable" reads
        // must never be promoted to a matched read.
        if (list.length === 0) return { status: "timeout", value: "" };
        return { status: "readable", value: canonicalizeVoices(list) };
      } catch (error) {
        return { status: statusFromException(error), value: "" };
      }
    };
    try {
      if (
        typeof speechSynthesis === "undefined" ||
        typeof speechSynthesis.getVoices !== "function"
      ) {
        const missing: Probe = { status: "unsupported", value: "" };
        return resolve([missing, missing]);
      }

      // Initialised to undefined so the cleanup closure can read them before the waiting
      // phase (which assigns them) has started.
      let poll: ReturnType<typeof setInterval> | undefined = undefined;
      let deadline: ReturnType<typeof setTimeout> | undefined = undefined;
      let secondTimer: ReturnType<typeof setTimeout> | undefined;
      let settled = false;
      /** Phase guard: onLoaded runs its body once, however many change events or poll ticks
       * race in, so it can never schedule a second second-read timer. */
      let loaded = false;
      const cleanup = () => {
        if (poll) clearInterval(poll);
        if (deadline) clearTimeout(deadline);
        if (secondTimer) clearTimeout(secondTimer);
        speechSynthesis.removeEventListener("voiceschanged", onChange);
      };
      const settle = (pair: [Probe, Probe]) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(pair);
      };
      /** A non-empty list was observed; stop waiting and take both bounded reads. */
      const onLoaded = () => {
        if (settled || loaded) return;
        loaded = true;
        const first = readCanonical();
        if (first.status !== "readable") {
          // The read refused, failed, or the list emptied again mid-race — confirm with an
          // immediate second attempt so a consistent refusal classifies as blocked while a
          // flapping list stays inconclusive.
          return settle([first, readCanonical()]);
        }
        // Waiting is over: drop the load-phase timers, keep only the second-read timer.
        if (poll) clearInterval(poll);
        if (deadline) clearTimeout(deadline);
        speechSynthesis.removeEventListener("voiceschanged", onChange);
        secondTimer = setTimeout(
          () => settle([first, readCanonical()]),
          VOICES_SECOND_READ_DELAY_MS
        );
      };
      const listKnown = () => {
        try {
          return speechSynthesis.getVoices().length > 0;
        } catch {
          // Let readCanonical classify the exception (blocked vs error) on the read path.
          return true;
        }
      };
      function onChange() {
        if (listKnown()) onLoaded();
      }

      if (listKnown()) return onLoaded();
      speechSynthesis.addEventListener("voiceschanged", onChange);
      poll = setInterval(() => {
        if (listKnown()) onLoaded();
      }, VOICES_POLL_MS);
      deadline = setTimeout(() => {
        const timedOut: Probe = { status: "timeout", value: "" };
        settle([timedOut, timedOut]);
      }, VOICES_LOAD_DEADLINE_MS);
    } catch (error) {
      const failed: Probe = { status: statusFromException(error), value: "" };
      resolve([failed, failed]);
    }
  });
}

/** Render the known constant and identifying oscillator into different time ranges of one
 * channel, then check and fingerprint the same `getChannelData()` result. The old check
 * rendered a separate buffer, so it could certify a surface other than the one being hashed. */
async function audioFp(): Promise<CheckedProbe> {
  try {
    if (typeof OfflineAudioContext === "undefined") {
      return { status: "unsupported", value: "", verification: TAMPER_UNAVAILABLE };
    }
    const ctx = new OfflineAudioContext(1, 44100, 44100);
    // ConstantSourceNode isn't in every engine. The oscillator could still render, but without
    // same-buffer verification it must not become a hash input.
    if (typeof ctx.createConstantSource !== "function") {
      return { status: "unsupported", value: "", verification: TAMPER_UNAVAILABLE };
    }

    // Two known-value windows, one on each side of the fingerprinted region, so noise aimed at
    // a single stretch of the buffer has to know this exact layout to slip past both. The
    // oscillator is stopped well before the second window: its compressor output measures
    // exactly zero from ~4k samples after the stop (verified empirically), so the second
    // window carries the constant tone alone and cannot pick up a decaying tail.
    const oscillatorStart = 4096;
    const oscillatorStop = 12288;
    const SENTINEL_SOURCES: [start: number, end: number][] = [
      [0, 2304],
      [30000, 32304],
    ];
    const SENTINEL_WINDOWS: [start: number, end: number][] = [
      [256, 2048],
      [30256, 32048],
    ];
    for (const [start, end] of SENTINEL_SOURCES) {
      const constant = ctx.createConstantSource();
      constant.offset.value = 1;
      constant.connect(ctx.destination);
      constant.start(start / ctx.sampleRate);
      constant.stop(end / ctx.sampleRate);
    }

    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = 10000;
    const comp = ctx.createDynamicsCompressor();
    osc.connect(comp);
    comp.connect(ctx.destination);
    osc.start(oscillatorStart / ctx.sampleRate);
    osc.stop(oscillatorStop / ctx.sampleRate);
    const buf = await ctx.startRendering();
    const data = buf.getChannelData(0);

    let altered = 0;
    let checked = 0;
    for (const [start, end] of SENTINEL_WINDOWS) {
      for (let i = start; i < end; i++) {
        checked++;
        if (data[i] !== 1) altered++;
      }
    }
    const verification: TamperProbe =
      altered > 0
        ? {
            status: "altered",
            evidence: `${altered} of ${checked} test samples did not match the tone we rendered`,
          }
        : { status: "clean" };

    let sum = 0;
    for (let i = 4500; i < 5000; i++) sum += Math.abs(data[i]);
    return {
      status: "readable",
      value: sum.toString(),
      verification,
      tamperEvidence: verification.evidence,
    };
  } catch (error) {
    return { status: statusFromException(error), value: "", verification: TAMPER_UNAVAILABLE };
  }
}

/** Real output device sample rate & channels — read from a throwaway AudioContext. Returns
 * typed probes: sentinel prose here used to be added with a default `readable` status and get
 * hashed, which is exactly the class of bug the typed statuses exist to prevent. */
function audioDeviceInfo(): { sampleRate: Probe; channels: Probe } {
  const both = (status: SignalStatus): { sampleRate: Probe; channels: Probe } => ({
    sampleRate: { status, value: "" },
    channels: { status, value: "" },
  });
  try {
    const Ctor =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return both("unsupported");
    const ctx = new Ctor();
    const channels = ctx.destination.maxChannelCount;
    const info = {
      sampleRate: { status: "readable" as SignalStatus, value: String(ctx.sampleRate) },
      channels: channels
        ? { status: "readable" as SignalStatus, value: String(channels) }
        : { status: "unsupported" as SignalStatus, value: "" },
    };
    // close() returns a promise that can reject; swallow it so cleanup can't surface as an
    // unhandled rejection mid-scan.
    void ctx.close().catch(() => {});
    return info;
  } catch (error) {
    return both(statusFromException(error));
  }
}

/**
 * Floating-point results of transcendental Math functions. Implementations differ between
 * engines, libm versions and CPU families, so the trailing digits are a stable, permission-free
 * signal — and one almost no privacy tool touches, which is exactly why it's worth showing.
 */
function mathFp(): Probe {
  try {
    const ops = [
      Math.acos(0.123456789),
      Math.acosh(1e300),
      Math.asin(0.123456789),
      Math.asinh(1),
      Math.atan(2),
      Math.atanh(0.5),
      Math.cbrt(100),
      Math.cosh(1),
      Math.expm1(1),
      Math.log1p(10),
      Math.sinh(1),
      Math.tan(-1e300),
      Math.tanh(1),
      Math.exp(1),
      Math.pow(Math.PI, -100),
    ];
    return { status: "readable", value: ops.map((n) => n.toString()).join(",") };
  } catch (error) {
    return { status: statusFromException(error), value: "" };
  }
}

/**
 * Usable screen area versus total screen area. The difference reveals the size and edge of the
 * taskbar or dock, which varies per user. Shown but never hashed: auto-hiding docks and
 * full-screen mode change it within a session.
 */
function screenFrameFp(): Probe {
  try {
    const insetV = screen.height - screen.availHeight;
    const insetH = screen.width - screen.availWidth;
    return {
      status: "readable",
      value: `${screen.availWidth}×${screen.availHeight} usable · ${insetH}px/${insetV}px reserved`,
    };
  } catch (error) {
    return { status: statusFromException(error), value: "" };
  }
}

/**
 * Bait elements styled like adverts. A content blocker hides them, so a zero height means one is
 * active. Deliberately reports presence only — naming the specific filter list would need a
 * curated selector-to-list map we don't maintain, and guessing would be worse than not saying.
 */
const AD_BAIT_CLASSES = [
  "adsbox",
  "ad-banner",
  "banner_ad",
  "pub_300x250",
  "sponsored-content",
  "textads",
];

function domBlockerFp(): Probe {
  const host = document.createElement("div");
  try {
    host.style.cssText = "position:absolute;left:-9999px;top:-9999px";
    const baits = AD_BAIT_CLASSES.map((cls) => {
      const el = document.createElement("div");
      el.className = cls;
      // Give every bait a real size, so zero height can only mean something hid it.
      el.style.cssText = "width:120px;height:32px;position:absolute";
      host.appendChild(el);
      return el;
    });
    document.body.appendChild(host);
    const blocked = baits.filter(
      (el) =>
        el.offsetHeight === 0 || el.offsetParent === null || getComputedStyle(el).display === "none"
    ).length;
    // Says what was observed, not what caused it: a user stylesheet or the site's own CSS can
    // hide these too, so this is not proof that a blocker is installed.
    return {
      status: "readable",
      value: blocked > 0 ? `${blocked} of ${baits.length} hidden` : "none hidden",
    };
  } catch (error) {
    return { status: statusFromException(error), value: "" };
  } finally {
    // In `finally` so an exceptional read can't leave bait elements behind in the page.
    host.remove();
  }
}

/**
 * Font *preferences*, distinct from which fonts are installed. Rendering a reference string in
 * each generic family and measuring its width exposes the user's chosen default font — a
 * setting almost nobody changes, and therefore highly identifying when it has been changed.
 *
 * Measured inside a bare same-origin iframe so the site's own stylesheet can't leak in.
 * Measured on the live body, "don't set a family" inherited this site's webfont instead of the
 * browser default, which baked Proxidize's CSS (and even its webfont load timing) into what is
 * supposed to be a *browser* signal. Technique adapted from FingerprintJS's fontPreferences
 * source, which isolates in an iframe for the same reason.
 */
function fontPreferencesFp(): Probe {
  const iframe = document.createElement("iframe");
  try {
    const families = ["standard", "sans-serif", "serif", "monospace", "cursive", "fantasy"];
    // visibility:hidden (not display:none) keeps layout running inside the frame.
    iframe.style.cssText =
      "position:absolute;left:-9999px;top:0;width:512px;height:128px;visibility:hidden;border:0";
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("tabindex", "-1");
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    if (!doc?.body) return { status: "error", value: "" };
    const span = doc.createElement("span");
    span.textContent = "mmMwWLliI0O&1";
    // A large size amplifies sub-pixel metric differences into whole pixels.
    span.style.cssText = "font-size:48px;white-space:nowrap";
    doc.body.appendChild(span);
    const widths = families.map((family) => {
      // "standard" means "don't set a family at all", i.e. the browser's default.
      span.style.fontFamily = family === "standard" ? "" : family;
      return `${family}:${span.getBoundingClientRect().width.toFixed(2)}`;
    });
    return { status: "readable", value: widths.join(",") };
  } catch (error) {
    return { status: statusFromException(error), value: "" };
  } finally {
    iframe.remove();
  }
}

/**
 * Candidate-font detection: which of ten common typefaces render differently from the generic
 * fallbacks. This tests *detection of ten candidates*, not the user's complete installed-font
 * inventory — CSS Fonts explicitly lets browsers withhold user-installed fonts, so even a
 * perfectly repeatable list is only what this site can observe.
 *
 * Measured inside a bare same-origin iframe for the same reason as `fontPreferencesFp`: on the
 * live body, the page's own stylesheet (webfonts, letter-spacing, `font-family … !important`)
 * leaks into what is supposed to be a *system* signal. Text properties that could vary the
 * measurement are reset explicitly, and widths are compared at subpixel precision rather than
 * the integer-rounded `offsetWidth`, which could hide a sub-pixel metric difference.
 */
function fontsFp(): Probe {
  const iframe = document.createElement("iframe");
  try {
    const bases = ["monospace", "sans-serif", "serif"];
    const candidates = [
      "Arial",
      "Helvetica",
      "Times New Roman",
      "Courier New",
      "Georgia",
      "Verdana",
      "Tahoma",
      "Comic Sans MS",
      "Trebuchet MS",
      "Impact",
    ];
    // visibility:hidden (not display:none) keeps layout running inside the frame.
    iframe.style.cssText =
      "position:absolute;left:-9999px;top:0;width:512px;height:128px;visibility:hidden;border:0";
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("tabindex", "-1");
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    if (!doc?.body) return { status: "error", value: "" };
    const span = doc.createElement("span");
    span.textContent = "mmmmmmmmmmlli";
    span.style.cssText =
      "position:absolute;left:0;top:0;font-size:72px;font-weight:normal;font-style:normal;" +
      "font-variant:normal;letter-spacing:normal;word-spacing:normal;text-transform:none;" +
      "white-space:nowrap";
    doc.body.appendChild(span);
    const width = () => span.getBoundingClientRect().width;
    const baseW: Record<string, number> = {};
    for (const b of bases) {
      span.style.fontFamily = b;
      baseW[b] = width();
    }
    const detected = candidates.filter((f) =>
      bases.some((b) => {
        span.style.fontFamily = `'${f}',${b}`;
        return width() !== baseW[b];
      })
    );
    // Matching none of the ten is a real answer, not proof of blocking: a minimal Linux install,
    // an Android device or a kiosk image legitimately has none of them. Calling it "blocked"
    // used to award a green defence for owning an unusual font set.
    return { status: "readable", value: JSON.stringify(detected) };
  } catch (error) {
    return { status: statusFromException(error), value: "" };
  } finally {
    iframe.remove();
  }
}

// --- Devices, permissions, storage, battery ---------------------------------------------

interface MediaDeviceCounts {
  cameras: number;
  microphones: number;
  speakers: number;
}

/** Device counts only — kinds enumerate without permission; labels (which would need a
 * permission prompt) are never read. */
/** Distinguishes "the API isn't here" from "the call failed" — collapsing both into null meant
 * a browser without the API was reported as actively blocking it. */
async function mediaDeviceCounts(): Promise<{
  status: SignalStatus;
  counts: MediaDeviceCounts | null;
}> {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return { status: "unsupported", counts: null };
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      status: "readable",
      counts: {
        cameras: devices.filter((d) => d.kind === "videoinput").length,
        microphones: devices.filter((d) => d.kind === "audioinput").length,
        speakers: devices.filter((d) => d.kind === "audiooutput").length,
      },
    };
  } catch (error) {
    return { status: statusFromException(error), counts: null };
  }
}

// Permission *names* beyond the TS union (camera, microphone, midi, clipboard-read) are
// valid in Chromium — each query is individually guarded since support varies per browser.
const PERMISSION_CHECKS: [label: string, name: string][] = [
  ["Notifications", "notifications"],
  ["Geolocation", "geolocation"],
  ["Camera", "camera"],
  ["Microphone", "microphone"],
  ["Clipboard read", "clipboard-read"],
  ["MIDI", "midi"],
];

async function permissionState(name: string): Promise<string> {
  try {
    if (!navigator.permissions?.query) return "unsupported";
    const status = await navigator.permissions.query({ name: name as PermissionName });
    return status.state;
  } catch (error) {
    return error instanceof TypeError ? "unsupported" : statusFromException(error);
  }
}

function webStorageAvailable(
  kind: "localStorage" | "sessionStorage"
): Extract<SignalStatus, "readable" | "blocked" | "error"> {
  try {
    const store = window[kind];
    const k = "__fp_probe__";
    store.setItem(k, "1");
    store.removeItem(k);
    return "readable";
  } catch (error) {
    return statusFromException(error);
  }
}

async function storageQuota(): Promise<Probe> {
  try {
    if (!navigator.storage?.estimate) return { status: "unsupported", value: "" };
    const { quota, usage } = await navigator.storage.estimate();
    if (!quota) return { status: "unsupported", value: "" };
    const quotaGb = (quota / 2 ** 30).toFixed(1);
    const usedMb = ((usage ?? 0) / 2 ** 20).toFixed(1);
    return { status: "readable", value: `${quotaGb} GB quota · ${usedMb} MB used` };
  } catch (error) {
    return { status: statusFromException(error), value: "" };
  }
}

interface BatteryManager {
  charging: boolean;
  level: number;
  chargingTime: number;
  dischargingTime: number;
}

function formatDuration(seconds: number): string | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// --- Consistency checks ------------------------------------------------------------------

function uaOsFamily(ua: string): string {
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  // Desktop-class iPadOS Safari sends a Macintosh UA. A real Mac reports no touch points, so
  // Macintosh + multi-touch is an iPad — without this it would be labelled macOS.
  if (/Macintosh/.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1)
    return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/CrOS/.test(ua)) return "Chrome OS";
  if (/Windows/.test(ua)) return "Windows";
  if (/Mac OS X|Macintosh/.test(ua)) return "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return "unknown";
}

/** Graphics *backends* are OS-exclusive in a way vendor names are not, so a backend that
 * can't exist on the claimed OS is a real contradiction. Vendor allowlists were the wrong
 * tool: "ANGLE (NVIDIA ... Direct3D11)" contains "angle", which any macOS renderer also
 * contains, so a Windows renderer claiming macOS used to pass. */
const IMPOSSIBLE_BACKENDS: Record<string, { pattern: RegExp; name: string }[]> = {
  // Vulkan is deliberately absent here: MoltenVK provides Vulkan over Metal on macOS, so while
  // no stock browser reports it in a WebGL renderer string today, it isn't impossible.
  macOS: [
    { pattern: /direct3d|d3d\d/i, name: "Direct3D" },
    { pattern: /\bmesa\b|llvmpipe/i, name: "Mesa" },
  ],
  iOS: [{ pattern: /direct3d|d3d\d|\bmesa\b/i, name: "Direct3D/Mesa" }],
  Windows: [
    { pattern: /metal renderer|apple\s+m\d/i, name: "Apple Metal" },
    { pattern: /llvmpipe/i, name: "llvmpipe" },
  ],
  Android: [{ pattern: /direct3d|d3d\d|metal renderer/i, name: "Direct3D/Metal" }],
  Linux: [
    { pattern: /direct3d|d3d\d/i, name: "Direct3D" },
    { pattern: /metal renderer/i, name: "Apple Metal" },
  ],
  "Chrome OS": [{ pattern: /direct3d|d3d\d|metal renderer/i, name: "Direct3D/Metal" }],
};

/** Chromium reports "Chrome OS" with Google branding and "Chromium OS" otherwise —
 * the same OS family for consistency purposes. */
function canonicalOs(os: string): string {
  return os === "Chromium OS" ? "Chrome OS" : os;
}

interface ConsistencyInput {
  ua: string;
  navPlatform: string;
  webdriver: boolean;
  hasClientHints: boolean;
  chPlatform?: string;
  chArchitecture?: string;
  chBrands?: UABrand[];
  glRenderer: string;
  glStatus: SignalStatus;
  worker: WorkerSnapshot | null;
  mainTimezone: string;
  mainLanguages: string;
}

function buildConsistencyChecks(input: ConsistencyInput): ConsistencyCheck[] {
  const checks: ConsistencyCheck[] = [];
  const push = (
    label: string,
    status: ConsistencyCheck["status"],
    detail: string,
    comparison = true
  ) => checks.push({ label, status, detail, comparison });
  /** Record a check we could not run, so it is never silently counted as a pass. */
  const skip = (label: string, why: string, comparison = true) =>
    push(label, "skipped", why, comparison);

  // Automation flag — the loudest single tell for anti-bot systems, but not a contradiction:
  // it's one value being honest about itself, so it's marked as a non-comparison check.
  if (input.webdriver) {
    push("Automation flag", "bad", "navigator.webdriver is true and visible to every site.", false);
  } else {
    push("Automation flag", "ok", "navigator.webdriver is not set.", false);
  }

  // UA string vs Client Hints. Client Hints are Chromium-only, so on Firefox and Safari these
  // are recorded as skipped rather than quietly dropped from the total.
  const CH_ONLY = "Needs User-Agent Client Hints, which only Chromium browsers provide.";
  if (!input.hasClientHints) {
    skip("Browser version", CH_ONLY);
    skip("Operating system", CH_ONLY);
  } else {
    const uaMajor = /Chrom(?:e|ium)\/(\d+)/.exec(input.ua)?.[1];
    const chMajor = realBrand(input.chBrands)?.version.split(".")[0];
    if (!uaMajor || !chMajor) {
      skip("Browser version", "No comparable version number in the User-Agent and Client Hints.");
    } else if (uaMajor === chMajor) {
      push("Browser version", "ok", `User-Agent and Client Hints agree on version ${uaMajor}.`);
    } else {
      push(
        "Browser version",
        "bad",
        `User-Agent claims ${uaMajor} but Client Hints report ${chMajor}, a spoofed UA string.`
      );
    }

    const uaOs = uaOsFamily(input.ua);
    if (!input.chPlatform || uaOs === "unknown") {
      skip("Operating system", "Couldn't determine an OS family from both sources.");
    } else if (canonicalOs(uaOs) === canonicalOs(input.chPlatform)) {
      push("Operating system", "ok", `User-Agent and Client Hints agree on ${uaOs}.`);
    } else {
      push(
        "Operating system",
        "bad",
        `User-Agent looks like ${uaOs} but Client Hints report ${input.chPlatform}.`
      );
    }

    // Only meaningful on Apple Silicon, where the two APIs disagree by design.
    if (input.navPlatform === "MacIntel" && input.chArchitecture === "arm") {
      push(
        "Platform vs architecture",
        "ok",
        "MacIntel + arm is normal: Apple Silicon keeps navigator.platform as MacIntel for compatibility."
      );
    }
  }

  // Screen plausibility — spoofed environments often report impossible geometry.
  const w = screen.width;
  const h = screen.height;
  const dpr = window.devicePixelRatio;
  const screenIssues: string[] = [];
  if (w < 320 || h < 320 || w > 16384 || h > 16384) screenIssues.push("screen dimensions");
  // devicePixelRatio includes page zoom. A fixed threshold would label legitimate 300–400%
  // accessibility zoom on a HiDPI display as spoofing, so DPR is reported but not scored.
  if (![16, 24, 30, 32, 48].includes(screen.colorDepth))
    screenIssues.push(`color depth (${screen.colorDepth})`);
  if (window.innerWidth > w * 2) screenIssues.push("viewport larger than the screen");
  // Plausibility, not a comparison between two sources.
  if (screenIssues.length === 0) {
    push("Screen geometry", "ok", `${w}×${h} @${dpr}x is a plausible display.`, false);
  } else {
    push("Screen geometry", "warn", `Unusual screen setup: ${screenIssues.join(", ")}.`, false);
  }

  // GPU renderer vs platform. Only genuine contradictions are flagged: an unfamiliar vendor
  // is not evidence of anything (Asahi, Panfrost and V3D are all legitimate), so anything
  // without a contradiction is reported as consistent-so-far, never as a vendor match.
  const renderer = input.glRenderer;
  if (input.glStatus !== "readable") {
    skip(
      "GPU renderer",
      input.glStatus === "unsupported"
        ? "WebGL isn't available in this browser, so the renderer couldn't be compared."
        : "The WebGL renderer read was blocked, so it couldn't be compared."
    );
  } else {
    const os = canonicalOs(input.chPlatform || uaOsFamily(input.ua));
    const impossible = IMPOSSIBLE_BACKENDS[os]?.find((b) => b.pattern.test(renderer));
    if (/swiftshader|llvmpipe|software/i.test(renderer)) {
      push(
        "GPU renderer",
        "warn",
        `Software renderer (${renderer}), common in headless browsers and VMs.`
      );
    } else if (impossible) {
      push(
        "GPU renderer",
        "bad",
        `Reports a ${impossible.name} renderer, which stock browsers don't ship on ${os}: "${renderer}".`
      );
    } else if (IMPOSSIBLE_BACKENDS[os]) {
      push(
        "GPU renderer",
        "ok",
        `Nothing about "${renderer}" contradicts ${os}. Renderer strings are easily spoofed, so this only means no known contradiction was found.`
      );
    } else {
      skip("GPU renderer", `No renderer expectations are defined for ${os}.`);
    }
  }

  // Main thread vs worker scope. Values that must be identical in both, because they describe
  // the same machine — so a difference means something rewrote one of them.
  const CROSS_CONTEXT = "Cross-context values";
  if (!input.worker) {
    skip(CROSS_CONTEXT, "Couldn't start a worker to compare against (blocked or unsupported).");
  } else {
    const w = input.worker;
    const mismatches: string[] = [];
    // Compared unconditionally: guarding on truthiness let a spoof that blanks a worker value
    // slip through as "nothing to compare".
    if (w.userAgent !== input.ua) mismatches.push("User-Agent");
    if (w.hardwareConcurrency !== navigator.hardwareConcurrency) {
      mismatches.push(`CPU threads (${navigator.hardwareConcurrency} vs ${w.hardwareConcurrency})`);
    }
    if (w.platform !== input.navPlatform) mismatches.push("platform");
    if (w.timezone !== input.mainTimezone) {
      mismatches.push(`timezone (${input.mainTimezone} vs ${w.timezone})`);
    }
    if (w.languages !== input.mainLanguages) mismatches.push("languages");

    if (mismatches.length === 0) {
      push(
        CROSS_CONTEXT,
        "ok",
        "The page and a background worker report identical values, so nothing is rewriting them in one place only."
      );
    } else {
      // A warning, not a contradiction. Partial spoofing is the likeliest cause, but a browser
      // that farbles each context with its own seed would look the same, and there is no spec
      // rule that every hint must match across realms.
      push(
        CROSS_CONTEXT,
        "warn",
        `The page and a background worker disagree on ${mismatches.join(", ")}. Usually that means something rewrote the page's values and missed worker scope, though a browser that randomises each context separately would look the same.`
      );
    }
  }

  return checks;
}

// --- Collection ---------------------------------------------------------------------------

/** Thrown when the page isn't a secure context, so Web Crypto (and therefore hashing) can't
 * exist. Surfaced as its own message instead of blaming an extension. */
export class InsecureContextError extends Error {
  constructor() {
    super("Web Crypto requires HTTPS or localhost");
    this.name = "InsecureContextError";
  }
}

export async function collectFingerprint(): Promise<FingerprintReport> {
  // Preflight: SubtleCrypto is only exposed in secure contexts (localhost gets a dev
  // exception, a plain LAN address does not). Fail immediately rather than running every
  // probe and then dying at the hashing step ~10 seconds later.
  if (typeof window !== "undefined" && (!window.isSecureContext || !crypto?.subtle)) {
    throw new InsecureContextError();
  }
  const startedAt = performance.now();
  const signals: FpSignal[] = [];
  const hashPairs: [string, string][] = [];

  interface AddOptions {
    /** Raw value to hash, when it differs from the displayed text. */
    raw?: string;
    /** False for values that legitimately change between runs (battery, permissions…). */
    hashed?: boolean;
    status?: SignalStatus;
    /** Pin a stable key when the display label may be reworded. */
    key?: string;
    /** Short annotation shown beside the value, e.g. proof it was tampered with. */
    note?: string;
    /** Typed claim strength of `note` — see FpSignal.noteKind. Defaults to "observation" when
     * a note is present, so forgetting it can only understate, never overstate. */
    noteKind?: FpSignal["noteKind"];
  }

  const usedKeys = new Set<string>();
  const add = (category: string, label: string, display: string, opts: AddOptions = {}) => {
    const status = opts.status ?? "readable";
    const hashed = (opts.hashed ?? true) && isHashableSignalStatus(status);
    const key = opts.key ?? SIGNAL_KEYS[label];
    // Enforced in every environment. A production fallback to the display label would let a
    // future copy edit silently change every visitor's hash, which is the whole failure the
    // registry exists to prevent.
    if (!key) {
      throw new Error(
        `fingerprint: no stable key registered for "${label}". Add it to SIGNAL_KEYS; ` +
          `keys must never be derived from display copy.`
      );
    }
    if (usedKeys.has(key)) {
      throw new Error(`fingerprint: duplicate signal key "${key}" (label "${label}").`);
    }
    usedKeys.add(key);
    signals.push({
      category,
      label,
      key,
      value: display,
      status,
      hashed,
      note: opts.note,
      noteKind: opts.note ? (opts.noteKind ?? "observation") : undefined,
    });
    // Structured pairs, so a value containing "|" or "=" can't forge another field.
    if (hashed) hashPairs.push([key, opts.raw ?? display]);
  };

  /**
   * A two-state row. The hash sees `"1"` or `"0"`; the reader sees whatever words fit best.
   * Rows like this used to hash their own display text ("working", "exposed", "enabled"), which
   * quietly made UI copy a hash input: renaming "exposed" to "available" would have changed
   * every visitor's snapshot. Booleans carry no information beyond the bit, so encode the bit.
   */
  const addFlag = (
    category: string,
    label: string,
    on: boolean,
    words: [onWord: string, offWord: string],
    opts: AddOptions = {}
  ) => add(category, label, on ? words[0] : words[1], { ...opts, raw: on ? "1" : "0" });

  /** Render an absent optional value without letting a display placeholder become a hash input. */
  const addOptional = (
    category: string,
    label: string,
    value: string | null | undefined,
    opts: AddOptions = {}
  ) =>
    add(category, label, value || "unavailable", {
      ...opts,
      raw: opts.raw ?? value ?? "",
      status: value ? (opts.status ?? "readable") : "unsupported",
    });

  const nav = navigator as Navigator & {
    deviceMemory?: number;
    connection?: { effectiveType?: string; downlink?: number };
    webdriver?: boolean;
    userAgentData?: NavigatorUAData;
    getBattery?: () => Promise<BatteryManager>;
  };

  // --- Start every independent probe at once ------------------------------------------------
  // None of these depend on each other, so awaiting them one at a time made a cold scan cost
  // the SUM of their budgets instead of approximately the slowest single one (~2s).
  const uaData = nav.userAgentData;
  const highEntropyPromise: Promise<{ ok: boolean; values: UADataValues }> = uaData
    ? (() => {
        let chTimer: ReturnType<typeof setTimeout> | undefined;
        return Promise.race([
          Promise.resolve()
            .then(() => uaData.getHighEntropyValues(HIGH_ENTROPY_HINTS))
            .then((v) => ({ ok: true, values: v }))
            .catch(() => ({ ok: false, values: {} as UADataValues })),
          new Promise<{ ok: boolean; values: UADataValues }>((resolve) => {
            chTimer = setTimeout(() => resolve({ ok: false, values: {} as UADataValues }), 1500);
          }),
        ]).finally(() => {
          if (chTimer) clearTimeout(chTimer);
        });
      })()
    : Promise.resolve({ ok: false, values: {} as UADataValues });
  const quotaPromise = withTimeout(storageQuota(), 1000, {
    status: "timeout" as SignalStatus,
    value: "",
  });
  // Each query carries its own deadline: one hung permission promise used to sink all six
  // already-resolved answers with it.
  const permissionsPromise = Promise.all(
    PERMISSION_CHECKS.map(
      async ([label, name]) =>
        [label, await withTimeout(permissionState(name), 1500, "timeout")] as const
    )
  );
  const audioPromise = Promise.all([
    withTimeout(audioFp(), 2000, CHECKED_TIMEOUT),
    withTimeout(audioFp(), 2000, CHECKED_TIMEOUT),
  ]);
  // speechVoicesProbes owns its own load deadline (~1.55s worst case) and cleans up its own
  // timers/listener; this outer bound is belt-and-braces and can never orphan the poll.
  const voicesTimeout: Probe = { status: "timeout", value: "" };
  const voicesPromise = withTimeout(speechVoicesProbes(), 2500, [voicesTimeout, voicesTimeout] as [
    Probe,
    Probe,
  ]);
  const devicesPromise = withTimeout(mediaDeviceCounts(), 1500, {
    status: "timeout" as SignalStatus,
    counts: null,
  });
  const batteryPromise: Promise<{
    status: SignalStatus;
    battery: BatteryManager | null;
  }> = nav.getBattery
    ? withTimeout<{ status: SignalStatus; battery: BatteryManager | null }>(
        Promise.resolve()
          .then(() => nav.getBattery?.())
          .then((battery) => ({
            status: battery ? ("readable" as const) : ("error" as const),
            battery: battery ?? null,
          }))
          .catch((error) => ({ status: statusFromException(error), battery: null })),
        1000,
        { status: "timeout", battery: null }
      )
    : Promise.resolve({ status: "unsupported", battery: null });
  const workerPromise = withTimeout(workerSnapshot(), 2000, null);

  // Browser & platform
  add("Browser & platform", "User agent", navigator.userAgent);
  // Labelled "Navigator platform" to distinguish it from the Client Hints platform row —
  // both are correct but come from different APIs (and can disagree, e.g. Apple Silicon).
  addOptional("Browser & platform", "Navigator platform", nav.platform);
  addFlag("Browser & platform", "Cookies", nav.cookieEnabled, ["enabled", "disabled"]);
  // Firefox removed the Do Not Track checkbox in 135 and Safari dropped the header years ago, so
  // on most current browsers this reads "unset" — which is itself the honest answer.
  const dnt = navigator.doNotTrack;
  add(
    "Browser & platform",
    "Do Not Track",
    dnt === "1" ? "enabled" : dnt === "0" ? "disabled" : "unset",
    { raw: dnt === "1" ? "1" : dnt === "0" ? "0" : "unset" }
  );
  addFlag("Browser & platform", "Automation", Boolean(nav.webdriver), ["yes", "no"]);
  if (typeof navigator.pdfViewerEnabled === "boolean") {
    addFlag("Browser & platform", "PDF viewer", navigator.pdfViewerEnabled, ["built-in", "none"]);
  } else {
    add("Browser & platform", "PDF viewer", "unavailable", { status: "unsupported" });
  }

  // Client Hints — high-entropy values must be requested explicitly and can reveal details
  // the classic APIs hide: e.g. on Apple Silicon, navigator.platform still reports "MacIntel"
  // for compatibility while Client Hints expose `architecture: arm`. Both are shown on purpose.
  let high: UADataValues = {};
  if (!uaData) {
    add("Client Hints", "Client Hints", "unsupported in this browser", { status: "unsupported" });
  } else {
    addOptional("Client Hints", "Brands", formatBrands(uaData.brands), {
      raw: uaData.brands?.length ? JSON.stringify(uaData.brands) : "",
    });
    addFlag("Client Hints", "Mobile", uaData.mobile, ["yes", "no"]);
    addOptional("Client Hints", "Client Hints platform", uaData.platform);
    // A timed-out high-entropy request used to leave "—" placeholders that were still hashed,
    // so one slow call silently changed the ID. `highOk` marks them all non-deterministic.
    const settled = await highEntropyPromise;
    const highOk = settled.ok;
    high = settled.values;
    // An omitted hint is visible as unavailable but excluded. A request failure is transient.
    // `machine` overrides what gets hashed for hints whose displayed value is a word rather
    // than the hint's own value, so rewording the row can never move anyone's snapshot.
    const ch = (label: string, value: string | undefined, machine?: () => string | undefined) =>
      highOk
        ? add("Client Hints", label, value || "unavailable", {
            raw: (machine ? machine() : value) || "",
            status: value ? "readable" : "unsupported",
          })
        : add("Client Hints", label, "timed out", { status: "timeout" });
    ch("Platform version", high.platformVersion);
    ch("Architecture", high.architecture);
    ch("Bitness", high.bitness);
    // uaFullVersion is deprecated in favour of fullVersionList; fall back to the first real
    // (non-GREASE) entry so the row stays useful when a browser drops the old field.
    ch("Full browser version", high.uaFullVersion || realBrand(high.fullVersionList)?.version);
    ch("Full version list", formatBrands(high.fullVersionList), () =>
      high.fullVersionList?.length ? JSON.stringify(high.fullVersionList) : undefined
    );
    ch("Model", high.model);
    // "yes"/"no" is display copy, so the hint's machine value is the bit, not the word.
    ch("WOW64", high.wow64 === undefined ? undefined : high.wow64 ? "yes" : "no", () =>
      high.wow64 === undefined ? undefined : high.wow64 ? "1" : "0"
    );
    ch("Form factors", high.formFactors?.length ? high.formFactors.join(", ") : undefined, () =>
      high.formFactors?.length ? JSON.stringify(high.formFactors) : undefined
    );
  }

  // Language & time
  addOptional("Language & time", "Languages", nav.languages?.join(", ") || nav.language);
  addOptional("Language & time", "Timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
  // Shown, never hashed: the offset flips twice a year at DST while the IANA zone above stays
  // put, so hashing it would change everyone's snapshot on two arbitrary nights.
  add("Language & time", "Timezone offset", `${new Date().getTimezoneOffset()} min`, {
    hashed: false,
  });

  // Screen & display
  // Hashed on the long edge first so rotating a phone doesn't produce a different snapshot;
  // the display order is kept for the human-readable value only.
  const [longEdge, shortEdge] = [screen.width, screen.height].sort((a, b) => b - a);
  add(
    "Screen & display",
    "Screen",
    `${screen.width}×${screen.height} @${window.devicePixelRatio}x · ${screen.colorDepth}-bit`,
    {
      // Numbers and separators only. The display string carries "@", "x" and "-bit" for the
      // reader; putting those in the hash would make the units part of everyone's identity.
      raw: [longEdge, shortEdge, window.devicePixelRatio, screen.colorDepth].join("/"),
    }
  );
  // These four are CSS media-query keywords in both the display and the hash, so the value the
  // reader sees is the spec token and there is nothing to translate.
  add("Screen & display", "Color scheme", mq("(prefers-color-scheme: dark)") ? "dark" : "light");
  add(
    "Screen & display",
    "Reduced motion",
    mq("(prefers-reduced-motion: reduce)") ? "reduce" : "no-preference"
  );
  addOptional("Screen & display", "Color gamut", colorGamut());
  addFlag("Screen & display", "HDR", mq("(dynamic-range: high)"), ["supported", "standard"]);
  const frame = screenFrameFp();
  add("Screen & display", "Usable area", frame.value || frame.status, {
    status: frame.status,
    // Auto-hiding docks and full-screen mode move this within a session.
    hashed: false,
  });

  // Input & CSS features
  const pointer = pointerType();
  addOptional(
    "Input & CSS",
    "Pointer",
    pointer === "fine"
      ? "fine (mouse / trackpad)"
      : pointer === "coarse"
        ? "coarse (touch)"
        : pointer,
    { raw: pointer }
  );
  addFlag("Input & CSS", "Hover", mq("(hover: hover)"), ["supported", "none"]);
  addFlag("Input & CSS", "Forced colors", mq("(forced-colors: active)"), ["active", "none"]);
  add("Input & CSS", "Contrast preference", contrastPreference());

  // Hardware & network
  addOptional(
    "Hardware & network",
    "CPU threads",
    nav.hardwareConcurrency ? String(nav.hardwareConcurrency) : undefined
  );
  addOptional(
    "Hardware & network",
    "Device memory",
    nav.deviceMemory ? `${nav.deviceMemory} GB` : undefined,
    { raw: nav.deviceMemory ? String(nav.deviceMemory) : "" }
  );
  add("Hardware & network", "Touch points", String(nav.maxTouchPoints ?? 0));
  // Network conditions change with connectivity, not the browser — shown but never hashed,
  // or the "stable on reload" promise breaks the moment someone switches Wi-Fi networks.
  const conn = nav.connection;
  add(
    "Hardware & network",
    "Network",
    conn?.effectiveType
      ? `${conn.effectiveType}${conn.downlink ? ` · ${conn.downlink} Mbps` : ""}`
      : "unavailable",
    { hashed: false, status: conn?.effectiveType ? "readable" : "unsupported" }
  );

  // Storage — support is a browser trait (hashed); quota reflects free disk space (not hashed).
  // Local/session storage are genuinely exercised (a write+delete probe), so "working" is
  // earned; IndexedDB and Cache API are presence checks and labelled like the API rows.
  const localStore = webStorageAvailable("localStorage");
  const sessionStore = webStorageAvailable("sessionStorage");
  const addStorageProbe = (label: string, status: SignalStatus) =>
    status === "readable"
      ? addFlag("Storage", label, true, ["working", "unavailable"], { status })
      : add("Storage", label, status, { status });
  addStorageProbe("LocalStorage", localStore);
  addStorageProbe("SessionStorage", sessionStore);
  addFlag("Storage", "IndexedDB", typeof indexedDB !== "undefined", ["exposed", "not exposed"], {
    status: typeof indexedDB !== "undefined" ? "readable" : "unsupported",
  });
  addFlag("Storage", "Cache API", "caches" in window, ["exposed", "not exposed"], {
    status: "caches" in window ? "readable" : "unsupported",
  });
  const quota = await quotaPromise;
  add("Storage", "Storage quota", quota.value || quota.status, {
    hashed: false,
    status: quota.status,
  });

  // API support — presence of powerful APIs is a stable browser/build trait. These only test
  // that the interface is exposed; permissions policy or flags may still prevent actual use,
  // so the rows say "exposed" rather than promising the API works.
  const apiExposed = (label: string, exposed: boolean) =>
    addFlag("API support", label, exposed, ["exposed", "not exposed"], {
      status: exposed ? "readable" : "unsupported",
    });
  apiExposed("WebGPU", "gpu" in navigator);
  apiExposed("WebUSB", "usb" in navigator);
  apiExposed("WebHID", "hid" in navigator);
  apiExposed("Web Bluetooth", "bluetooth" in navigator);
  apiExposed("Web Serial", "serial" in navigator);
  apiExposed("WebRTC", typeof RTCPeerConnection !== "undefined");

  // Permissions — user-granted states change over time, so they're shown but not hashed.
  const permissionStates = await permissionsPromise;
  for (const [label, state] of permissionStates) {
    add("Permissions", label, state, {
      hashed: false,
      status:
        state === "timeout" || state === "unsupported" || state === "blocked" || state === "error"
          ? state
          : "readable",
    });
  }

  // Canvas — sampled twice through fresh elements. Disagreement with an intact sentinel is
  // neutral variation (cause unknown); only a changed sentinel is modification evidence.
  // Agreement establishes repeatability only: seeded or stable substitutions can agree.
  const canvas = canvasFp();
  const canvas2 = canvasFp();
  const canvasTamper = [canvas, canvas2].find((probe) => probe.verification.status === "altered")
    ?.verification.evidence;
  const canvasAssessment = assessRepeatedProbes(canvas, canvas2, Boolean(canvasTamper));
  const canvasShort =
    canvas.status === "readable" ? (await sha256hex(canvas.value)).slice(0, 16) : canvas.status;
  add(
    "Canvas",
    "Canvas",
    canvasAssessment.outcome === "scrambled"
      ? `${canvasShort} (scrambled)`
      : canvasAssessment.randomizedInScan
        ? `${canvasShort} (varied)`
        : canvasAssessment.outcome === "unstable"
          ? `${canvasShort} (unstable)`
          : canvasShort,
    {
      // The hash represents what a site can repeatedly observe. The colour sentinel is
      // separate evidence: it can catch a changed test but cannot certify the unknown artwork.
      raw: canvas.value,
      hashed: canvasAssessment.hashable,
      status: canvasAssessment.signalStatus,
      note: canvasTamper
        ? `Scrambling detected: ${canvasTamper}`
        : canvasAssessment.randomizedInScan
          ? "Two reads differed; cause unknown; left out of the hash."
          : undefined,
      noteKind: canvasTamper ? "modification" : "observation",
    }
  );
  add(
    "Canvas",
    "Canvas details",
    canvas.status === "readable"
      ? canvasAssessment.outcome === "scrambled"
        ? `${CANVAS_W}×${CANVAS_H} test render · scrambled`
        : canvasAssessment.randomizedInScan
          ? `${CANVAS_W}×${CANVAS_H} test render · two reads differed`
          : canvasAssessment.outcome === "unstable"
            ? `${CANVAS_W}×${CANVAS_H} test render · reads inconclusive`
            : `${CANVAS_W}×${CANVAS_H} test render · read twice`
      : `canvas ${canvas.status} by the browser`,
    { hashed: false, status: canvasAssessment.signalStatus }
  );

  // WebGL — one context fills ten rows, so every row carries the context's status and a dead
  // context counts as one failed vector rather than ten failed reads.
  const glProbe = webglInfo();
  const glProbe2 = webglInfo();
  const gl = glProbe.info;
  const glStatus = webglProbeStatus(glProbe.status, glProbe.verification, glProbe.readbackFailed);
  const glStatus2 = webglProbeStatus(
    glProbe2.status,
    glProbe2.verification,
    glProbe2.readbackFailed
  );
  const glValue =
    glStatus === "readable"
      ? JSON.stringify({
          info: JSON.parse(webglInfoValue(glProbe.info)),
          readback: glProbe.readback,
        })
      : "";
  const glValue2 =
    glStatus2 === "readable"
      ? JSON.stringify({
          info: JSON.parse(webglInfoValue(glProbe2.info)),
          readback: glProbe2.readback,
        })
      : "";
  const glRead = { status: glStatus, value: glValue };
  const glRead2 = { status: glStatus2, value: glValue2 };
  const glVerification =
    [glProbe.verification, glProbe2.verification].find((probe) => probe.status === "altered") ??
    [glProbe.verification, glProbe2.verification].find((probe) => probe.status === "refused") ??
    [glProbe.verification, glProbe2.verification].find((probe) => probe.status === "clean");
  const glTamper = glVerification?.status === "altered" ? glVerification.evidence : undefined;
  const glAssessment = assessRepeatedProbes(glRead, glRead2, Boolean(glTamper));

  // WebGL 2 — a controlled render through a real vertex/fragment shader, run through two fresh
  // contexts. Where WebGL 2 doesn't exist this is a deterministic "unsupported" that the
  // Graphics vector drops rather than counting as a block against a working WebGL 1.
  const gl2Probe = webgl2ShaderFp();
  const gl2Probe2 = webgl2ShaderFp();
  const gl2Status = statusAfterVerification(gl2Probe.status, gl2Probe.verification);
  const gl2Status2 = statusAfterVerification(gl2Probe2.status, gl2Probe2.verification);
  const gl2Tamper = [gl2Probe, gl2Probe2].find((probe) => probe.verification.status === "altered")
    ?.verification.evidence;
  const gl2Assessment = assessRepeatedProbes(
    { status: gl2Status, value: gl2Status === "readable" ? gl2Probe.value : "" },
    { status: gl2Status2, value: gl2Status2 === "readable" ? gl2Probe2.value : "" },
    Boolean(gl2Tamper)
  );

  // The Graphics vector's aggregate gates *every* graphics hash input: when the folded vector
  // is inconclusive, even a sub-probe that individually repeated stays out of the hash, so the
  // report's "unstable results are excluded" claim holds with no partial-inclusion fine print.
  // An absent WebGL 2 drops out of the fold and costs nothing.
  const graphicsAssessment = combineVectorAssessments([glAssessment, gl2Assessment]);
  // A readable capability profile only enters the hash when a fresh context returns the same
  // profile. This catches per-read or per-context instability, but a stable substituted profile
  // is still just what the site can observe—there is no hidden hardware baseline to compare.
  const glOpts = (value: string) => ({
    status: glProbe.status === "readable" && !value ? ("unsupported" as const) : glProbe.status,
    hashed: graphicsAssessment.hashable && Boolean(value),
  });
  const glDisplay = (value: string) =>
    value || (glProbe.status === "readable" ? "unavailable" : glProbe.status);
  add("WebGL", "WebGL vendor", glDisplay(gl.vendor), glOpts(gl.vendor));
  add("WebGL", "WebGL renderer", glDisplay(gl.renderer), glOpts(gl.renderer));
  // The exact readback bytes checked above are the bytes hashed here when both fresh contexts
  // match. This check still says nothing about whether the separate capability strings are true.
  add(
    "WebGL",
    "WebGL readback",
    glAssessment.signalStatus !== "readable"
      ? glAssessment.outcome
      : glVerification?.status === "altered"
        ? "test changed"
        : glAssessment.randomizedInScan
          ? "two reads differed"
          : glVerification?.status === "clean"
            ? "matches what was drawn"
            : "test unavailable",
    {
      hashed: graphicsAssessment.hashable && Boolean(glProbe.readback),
      raw: glProbe.readback,
      status:
        glAssessment.signalStatus === "readable" && !glProbe.readback
          ? "unsupported"
          : glAssessment.signalStatus,
      note: glTamper ? `Scrambling detected: ${glTamper}` : undefined,
      noteKind: "modification",
    }
  );
  add("WebGL", "Max texture size", glDisplay(gl.maxTexture), glOpts(gl.maxTexture));
  add("WebGL", "Max viewport", glDisplay(gl.maxViewport ? gl.maxViewport.replace("/", "×") : ""), {
    ...glOpts(gl.maxViewport),
    raw: gl.maxViewport,
  });
  add("WebGL", "Max renderbuffer size", glDisplay(gl.maxRenderbuffer), glOpts(gl.maxRenderbuffer));
  const vertexLimits =
    gl.vertexAttribs && gl.varyingVectors && gl.vertexTextureUnits
      ? `${gl.vertexAttribs} attribs · ${gl.varyingVectors} varying · ${gl.vertexTextureUnits} vertex tex units`
      : "";
  add("WebGL", "Vertex limits", glDisplay(vertexLimits), {
    ...glOpts(vertexLimits),
    raw: [gl.vertexAttribs, gl.varyingVectors, gl.vertexTextureUnits].join("/"),
  });
  add(
    "WebGL",
    "Combined texture units",
    glDisplay(gl.combinedTextureUnits),
    glOpts(gl.combinedTextureUnits)
  );
  const [shaderPrecision, shaderRangeMin, shaderRangeMax] = gl.shaderPrecision.split("/");
  const shaderPrecisionDisplay = gl.shaderPrecision
    ? `highp float · precision ${shaderPrecision} · range 2^${shaderRangeMin} to 2^${shaderRangeMax}`
    : "";
  add("WebGL", "Shader precision", glDisplay(shaderPrecisionDisplay), {
    ...glOpts(gl.shaderPrecision),
    raw: gl.shaderPrecision,
  });
  const extensionCount = glProbe.status === "readable" ? JSON.parse(gl.extensions).length : 0;
  add(
    "WebGL",
    "WebGL extensions",
    glProbe.status === "readable" ? `${extensionCount} supported` : glProbe.status,
    { ...glOpts(gl.extensions), raw: gl.extensions }
  );

  add(
    "WebGL",
    "WebGL 2 shader",
    gl2Assessment.outcome === "unavailable"
      ? "unsupported"
      : gl2Assessment.outcome === "blocked"
        ? "blocked"
        : gl2Tamper
          ? "test changed"
          : gl2Assessment.randomizedInScan
            ? "two renders differed"
            : gl2Assessment.outcome === "unstable"
              ? "reads inconclusive"
              : "matches what was drawn",
    {
      // The exact shader-output bytes are a hash component only when both fresh contexts
      // returned identical pixels AND the whole Graphics vector is conclusive. A match is
      // repeatability, not proof the GPU strings are true.
      hashed: graphicsAssessment.hashable && Boolean(gl2Probe.value),
      raw: gl2Probe.value,
      status: gl2Assessment.signalStatus,
      note: gl2Tamper ? `Scrambling detected: ${gl2Tamper}` : undefined,
      noteKind: "modification",
    }
  );

  // Audio — sampled twice through fresh OfflineAudioContexts for the same reason as canvas.
  // Both renders start together: sequential awaits doubled the worst-case wait, which showed up
  // as a long "reading…" state on browsers that render offline audio slowly.
  const [audio, audio2] = await audioPromise;
  const audioTamper = [audio, audio2].find((probe) => probe.verification.status === "altered")
    ?.verification.evidence;
  const audioAssessment = assessRepeatedProbes(audio, audio2, Boolean(audioTamper));
  const audioShort =
    audio.status === "readable" ? (await sha256hex(audio.value)).slice(0, 16) : audio.status;
  add(
    "Audio",
    "Audio",
    audioAssessment.outcome === "scrambled"
      ? `${audioShort} (scrambled)`
      : audioAssessment.randomizedInScan
        ? `${audioShort} (varied)`
        : audioAssessment.outcome === "unstable"
          ? `${audioShort} (unstable)`
          : audioShort,
    {
      // As with canvas, repeatability controls hash inclusion. The known constant is only
      // evidence about the test tone and does not verify the separate oscillator samples.
      raw: audio.value,
      hashed: audioAssessment.hashable,
      status: audioAssessment.signalStatus,
      note: audioTamper
        ? `Scrambling detected: ${audioTamper}`
        : audioAssessment.randomizedInScan
          ? "Two reads differed; cause unknown; left out of the hash."
          : undefined,
      noteKind: audioTamper ? "modification" : "observation",
    }
  );
  // Device metadata is shown but deliberately kept out of the hash: sample rate and channel
  // count follow the *active output device* (they change when a headset or dock is plugged in)
  // and are read once from a live AudioContext, so the repeated OfflineAudioContext probe above
  // never verifies them. Hashing a single unverified read would let ordinary device changes —
  // or one flaky read — move the snapshot while the Sound verdict claimed repeatability.
  const audioDev = audioDeviceInfo();
  add(
    "Audio",
    "Sample rate",
    audioDev.sampleRate.value ? `${audioDev.sampleRate.value} Hz` : audioDev.sampleRate.status,
    {
      status: audioDev.sampleRate.status,
      hashed: false,
    }
  );
  add("Audio", "Output channels", audioDev.channels.value || audioDev.channels.status, {
    status: audioDev.channels.status,
    hashed: false,
  });
  addFlag(
    "Audio",
    "OfflineAudioContext",
    typeof OfflineAudioContext !== "undefined",
    ["supported", "unsupported"],
    { status: typeof OfflineAudioContext !== "undefined" ? "readable" : "unsupported" }
  );

  // Maths — engine/CPU floating-point differences, hashed since they're fully deterministic.
  const maths = mathFp();
  const mathsShort =
    maths.status === "readable" ? (await sha256hex(maths.value)).slice(0, 16) : maths.status;
  add("Maths", "Float results", mathsShort, { status: maths.status, raw: maths.value });
  add(
    "Maths",
    "Functions tested",
    maths.status === "readable" ? "15 transcendental" : maths.status,
    {
      status: maths.status,
      hashed: false,
    }
  );

  // Content blocker — about the extensions installed, not the browser itself, so never hashed.
  const blocker = domBlockerFp();
  add("Content blocking", "Ad-style elements", blocker.value || blocker.status, {
    status: blocker.status,
    hashed: false,
  });

  // Fonts — both probes sampled twice through fresh DOM elements/iframes, like canvas and
  // audio. Matching reads enter the hash; differing readable reads are instability (the value
  // changed during the scan), never conclusive scrambling — and even a matching list is only
  // what the site can observe, which a browser may filter or standardize wholesale.
  const fonts = fontsFp();
  const fonts2 = fontsFp();
  const fontsAssessment = assessExposureProbes(fonts, fonts2);
  const fontPrefs = fontPreferencesFp();
  const fontPrefs2 = fontPreferencesFp();
  const fontPrefsAssessment = assessExposureProbes(fontPrefs, fontPrefs2);
  // As with Graphics, the folded Fonts vector gates both rows' hash inclusion: if either
  // sub-probe is blocked or unstable the vector is inconclusive and *neither* row is hashed,
  // so a partially-failed vector can never leave half an identity in the snapshot while the
  // report says its unstable result was excluded.
  const fontsVectorAssessment = combineVectorAssessments([fontsAssessment, fontPrefsAssessment]);
  const detectedFonts =
    fonts.status === "readable" ? (JSON.parse(fonts.value) as string[]) : ([] as string[]);
  add(
    "Fonts",
    "Fonts",
    fonts.status === "readable"
      ? detectedFonts.join(", ") || "none of the ten tested"
      : fonts.status,
    {
      raw: fonts.value,
      hashed: fontsVectorAssessment.hashable,
      status: fontsAssessment.signalStatus,
      note: fontsAssessment.randomizedInScan
        ? "Two reads differed during the scan; shown from the first read, left out of the hash."
        : undefined,
    }
  );
  add(
    "Fonts",
    "Default font metrics",
    fontPrefs.status === "readable"
      ? `${fontPrefs.value.split(",").length} families measured`
      : fontPrefs.status,
    {
      raw: fontPrefs.value,
      hashed: fontsVectorAssessment.hashable,
      status: fontPrefsAssessment.signalStatus,
      note: fontPrefsAssessment.randomizedInScan
        ? "Two measurements differed during the scan; left out of the hash."
        : undefined,
    }
  );

  // Media
  const [voices, voices2] = await voicesPromise;
  const voicesAssessment = assessExposureProbes(voices, voices2);
  const voiceCount =
    voices.status === "readable" ? (JSON.parse(voices.value) as VoiceRecord[]).length : 0;
  // Never hashed, even when both normalized reads match. The voice list loads asynchronously
  // and is environmentally volatile (OS voice packs install and update out-of-band), so it is
  // frequently still empty or different on a cold first scan — hashing it made the first ID of
  // a fresh browser session differ from every later one, and made an innocent change look like
  // privacy protection kicking in. It stays visible as a strong signal, just not a hash input.
  add(
    "Media",
    "Speech voices",
    voicesAssessment.outcome === "read"
      ? `${voiceCount} voice${voiceCount === 1 ? "" : "s"} · read twice`
      : voicesAssessment.randomizedInScan
        ? "changed during the scan"
        : voicesAssessment.outcome === "unavailable"
          ? "not exposed"
          : voicesAssessment.signalStatus === "timeout"
            ? "not known yet (timed out)"
            : voicesAssessment.signalStatus,
    {
      raw: voices.value,
      status: voicesAssessment.signalStatus,
      hashed: false,
      note: voicesAssessment.randomizedInScan
        ? "Two reads returned different inventories. Instability, not proof of scrambling."
        : undefined,
    }
  );
  const codecs = codecsFp();
  const codecNames =
    codecs.status === "readable" ? (JSON.parse(codecs.value) as string[]) : ([] as string[]);
  add(
    "Media",
    "Video codecs",
    codecs.status === "readable" ? codecNames.join(", ") || "none" : codecs.status,
    {
      status: codecs.status,
      raw: codecs.value,
    }
  );

  // Media devices — counts only (never labels), and not hashed: plugging in a headset
  // shouldn't change a "stable" fingerprint. Until capture permission is granted, the spec
  // exposes at most one device per kind, so pre-permission counts mean "present", not "exact".
  const deviceProbe = await devicesPromise;
  const devices = deviceProbe.counts;
  if (devices) {
    const permMap = new Map<string, string>(permissionStates);
    const deviceCount = (n: number, permLabel: string) =>
      n > 0 && permMap.get(permLabel) !== "granted" ? `${n}+ (pre-permission)` : String(n);
    add("Media devices", "Cameras", deviceCount(devices.cameras, "Camera"), { hashed: false });
    add("Media devices", "Microphones", deviceCount(devices.microphones, "Microphone"), {
      hashed: false,
    });
    // Speaker enumeration is gated separately from microphone permission (Audio Output Devices
    // API), so this is reported as a lower bound rather than tied to a capture permission.
    add(
      "Media devices",
      "Speakers",
      devices.speakers > 0 ? `at least ${devices.speakers} visible` : "0",
      { hashed: false }
    );
  } else {
    add(
      "Media devices",
      "Media devices",
      deviceProbe.status === "unsupported" ? "not exposed" : deviceProbe.status,
      { hashed: false, status: deviceProbe.status }
    );
  }

  // Battery — Chromium only; highly volatile, so never hashed. Always the same three rows
  // whatever the read returned: a successful read used to add rows a timed-out one didn't,
  // so the "N properties" total wobbled between runs for no real reason.
  if (nav.getBattery) {
    const batteryProbe = await batteryPromise;
    const { battery } = batteryProbe;
    const batteryStatus = batteryProbe.status;
    const unavailableBattery = batteryStatus === "timeout" ? "timed out" : batteryStatus;
    add(
      "Power",
      "Battery level",
      battery ? `${Math.round(battery.level * 100)}%` : unavailableBattery,
      {
        hashed: false,
        status: batteryStatus,
      }
    );
    add("Power", "Charging", battery ? (battery.charging ? "yes" : "no") : unavailableBattery, {
      hashed: false,
      status: batteryStatus,
    });
    const remaining = battery
      ? formatDuration(battery.charging ? battery.chargingTime : battery.dischargingTime)
      : null;
    const remainingStatus: SignalStatus = battery && !remaining ? "unsupported" : batteryStatus;
    add(
      "Power",
      battery?.charging ? "Time to full" : "Time to empty",
      remaining ?? (battery ? "not reported" : unavailableBattery),
      { hashed: false, status: remainingStatus }
    );
  }

  const consistency = buildConsistencyChecks({
    ua: navigator.userAgent,
    navPlatform: nav.platform || "",
    webdriver: Boolean(nav.webdriver),
    hasClientHints: Boolean(uaData),
    chPlatform: uaData?.platform,
    chArchitecture: high.architecture,
    chBrands: uaData?.brands,
    glRenderer: gl.renderer,
    glStatus: glProbe.status,
    worker: await workerPromise,
    mainTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    mainLanguages: nav.languages?.join(", ") || nav.language || "",
  });

  // Vector-level truth: five strong vectors, each with one status. A dead WebGL context is
  // one blocked vector, not eight blocked rows. Graphics folds the WebGL 1 and WebGL 2
  // sub-probes together (an absent WebGL 2 drops out instead of blocking the vector); Fonts
  // folds the installed-font and default-metrics probes so one successful sub-probe can never
  // conceal a blocked or unstable sibling.
  const graphicsTamper = glTamper ?? gl2Tamper;
  const vectors: VectorState[] = [
    {
      key: "canvas",
      label: "Draws",
      status: canvasAssessment.signalStatus,
      outcome: canvasAssessment.outcome,
      randomizedInScan: canvasAssessment.randomizedInScan,
      inconclusive: canvasAssessment.inconclusive,
      repeatableInScan: canvasAssessment.repeatableInScan,
      tamperEvidence: canvasTamper,
      raw: canvas.value,
    },
    {
      key: "webgl",
      label: "Graphics",
      status: graphicsAssessment.signalStatus,
      outcome: graphicsAssessment.outcome,
      randomizedInScan: graphicsAssessment.randomizedInScan,
      inconclusive: graphicsAssessment.inconclusive,
      repeatableInScan: graphicsAssessment.repeatableInScan,
      tamperEvidence: graphicsTamper,
      raw: JSON.stringify([glValue, gl2Probe.value]),
    },
    {
      key: "audio",
      label: "Sound",
      status: audioAssessment.signalStatus,
      outcome: audioAssessment.outcome,
      randomizedInScan: audioAssessment.randomizedInScan,
      inconclusive: audioAssessment.inconclusive,
      repeatableInScan: audioAssessment.repeatableInScan,
      tamperEvidence: audioTamper,
      raw: audio.value,
    },
    {
      key: "fonts",
      label: "Fonts",
      status: fontsVectorAssessment.signalStatus,
      outcome: fontsVectorAssessment.outcome,
      randomizedInScan: fontsVectorAssessment.randomizedInScan,
      inconclusive: fontsVectorAssessment.inconclusive,
      repeatableInScan: fontsVectorAssessment.repeatableInScan,
      exposureOnly: true,
      raw: JSON.stringify([fonts.value, fontPrefs.value]),
    },
    {
      key: "voices",
      label: "Voices",
      status: voicesAssessment.signalStatus,
      outcome: voicesAssessment.outcome,
      randomizedInScan: voicesAssessment.randomizedInScan,
      inconclusive: voicesAssessment.inconclusive,
      repeatableInScan: voicesAssessment.repeatableInScan,
      exposureOnly: true,
      raw: voices.value,
    },
  ];

  const meta: FingerprintMeta = {
    signalCount: signals.length,
    hashedCount: hashPairs.length,
    vectorsRead: vectors.filter((v) => v.outcome === "read").length,
    vectorsReadable: vectors.filter((v) => v.outcome === "read" || v.outcome === "scrambled")
      .length,
    vectorsBlocked: vectors.filter((v) => v.outcome === "blocked").length,
    vectorsUnavailable: vectors.filter((v) => v.outcome === "unavailable").length,
    vectorsScrambled: vectors.filter((v) => v.outcome === "scrambled").length,
    vectorsUnstable: vectors.filter((v) => v.outcome === "unstable").length,
    vectorsTotal: vectors.length,
    failedRows: signals.filter(
      (s) => s.status === "blocked" || s.status === "error" || s.status === "timeout"
    ).length,
    durationMs: Math.round(performance.now() - startedAt),
    algorithmVersion: FINGERPRINT_ALGORITHM_VERSION,
  };

  // Canonical encoding: sorted by stable key so collector order can't change the ID, versioned
  // so a future change to the inputs is explicit, and structured so a value containing "|" or
  // "=" can't imitate another field.
  const canonical = canonicalFingerprintInput(hashPairs);
  const hashInput = JSON.parse(canonical) as {
    v: string;
    components: [key: string, value: string][];
  };
  const hash = await sha256hex(canonical);
  return {
    hash,
    signals,
    consistency,
    vectors,
    meta,
    hashInput,
  };
}
